import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, FileText, ChevronDown, ChevronUp, User, Calendar, Building2, Hash, Download, Database, X, Loader2, File, FileStack, List, Gavel, PlayCircle, Clock, BookOpen, RefreshCw, FileEdit, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useSessionStore, useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { useCompactMode } from '@/hooks/useCompactMode';
import { patentApi, taskApi, documentApi, resourceApi, draftApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';
import { DocumentViewer } from './DocumentViewer';
import { LineNumberedTextarea } from './LineNumberedTextarea';
import { exportDraftToDocx } from '@/utils/draftExport';
import { calculateOADeadlines, daysUntilDeadline } from '@/utils/deadlineCalculator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { OA_TASKS, OATask } from '@/constants/oaTasks';
import { OA_EMAIL_TEMPLATES } from '@/config/emailTemplates';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import { OAEmailResultView } from './task-displays/OAEmailResultView';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ExaminationPageProps {
  onDocumentSelect: (doc: any) => void;
  initialApplicationNumber?: string;
  initialDocument?: any;
  onNavigateToChat?: () => void;
}

interface ProsecutionDoc {
  date: string;
  rawDate: Date;
  pdfUrl: string | null;
  description: string;
  documentCode: string;
  docId: string;
}

export function ExaminationPage({ onDocumentSelect, initialApplicationNumber, initialDocument, onNavigateToChat }: ExaminationPageProps) {
  // Helper functions for parsing task results
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;

    // Check multiple possible property names where the actual data might be stored
    // Priority: result > output > analysis > use the object itself
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;

    // If none of the above exist, assume the object itself is the data
    return resultObj;
  };

  const getParsedResult = (output: any) => {
    if (!output) return null;

    // If it's already an object, return it
    if (typeof output === 'object') return output;

    // If it's a string, try to parse it as JSON
    if (typeof output === 'string') {
      // Check if it looks like a Python dictionary (starts with { and contains single quotes)
      if (!output.trim().startsWith('{') || !output.includes("'")) {
        return output;
      }

      try {
        // Try to parse as JSON first
        return JSON.parse(output);
      } catch {
        // If parsing fails, try to evaluate as Python dict string
        try {
          // More robust Python to JSON conversion
          // Handle single quotes more carefully to avoid breaking contractions
          let jsonStr = output;

          // Replace single quotes only in structural positions (keys, values, arrays)
          jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');  // Keys
          jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');  // Values after colons
          jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');  // Array first item
          jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');  // Array other items
          jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');  // Array last item

          // Handle Python boolean and null values
          jsonStr = jsonStr.replace(/True/g, 'true');
          jsonStr = jsonStr.replace(/False/g, 'false');
          jsonStr = jsonStr.replace(/None/g, 'null');

          return JSON.parse(jsonStr);
        } catch (e) {
          console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
          // If all parsing fails, return the string as-is
          return output;
        }
      }
    }

    return output;
  };

  const [selectedApplicationNumber, setSelectedApplicationNumber] = useState<string | null>(null);
  const [showApplicationSelector, setShowApplicationSelector] = useState(false);
  const [applicationSearchTerm, setApplicationSearchTerm] = useState('');
  const [showDetails, setShowDetails] = useState(true);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(300); // Width in pixels
  const [isRefreshingApp, setIsRefreshingApp] = useState(false);

  // Resizable panel widths
  const [middlePanelWidth, setMiddlePanelWidth] = useState(35); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const [isResizingLeftPanel, setIsResizingLeftPanel] = useState(false);

  // Prosecution documents state
  const [prosecutionDocs, setProsecutionDocs] = useState<ProsecutionDoc[]>([]);
  const [oaDocs, setOaDocs] = useState<ProsecutionDoc[]>([]);
  const [claimsDocs, setClaimsDocs] = useState<ProsecutionDoc[]>([]);
  const [specDocs, setSpecDocs] = useState<ProsecutionDoc[]>([]);
  const [loadingProsecution, setLoadingProsecution] = useState(false);

  // Selected documents
  const [selectedOfficeActionDoc, setSelectedOfficeActionDoc] = useState<ProsecutionDoc | null>(null);
  const [selectedProsDoc, setSelectedProsDoc] = useState<ProsecutionDoc | null>(null);

  // Office Action deadlines
  const [oaDeadlines, setOaDeadlines] = useState<{
    threeMonthDeadline: Date;
    sixMonthDeadline: Date;
    threeMonthDeadlineFormatted: string;
    sixMonthDeadlineFormatted: string;
  } | null>(null);

  // PDF viewer state
  const [oaPdfUrl, setOaPdfUrl] = useState<string | null>(null);
  const [prosPdfUrl, setProsPdfUrl] = useState<string | null>(null);
  const [oaNumPages, setOaNumPages] = useState<number>(0);
  const [prosNumPages, setProsNumPages] = useState<number>(0);
  const [oaScale, setOaScale] = useState(1.0);
  const [prosScale, setProsScale] = useState(1.0);
  const [loadingOaPdf, setLoadingOaPdf] = useState(false);
  const [loadingProsPdf, setLoadingProsPdf] = useState(false);
  const [oaPdfStatus, setOaPdfStatus] = useState<string>('');
  const [prosPdfStatus, setProsPdfStatus] = useState<string>('');

  // Task state
  const [selectedTask, setSelectedTask] = useState<OATask | null>(null);
  const [taskProcessing, setTaskProcessing] = useState(false);
  const [taskResult, setTaskResult] = useState<any | null>(null);
  const [showTaskResult, setShowTaskResult] = useState(false);
  const [showTaskPanel, setShowTaskPanel] = useState(false);

  // OA Reporting Email template selection
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('standard');
  const [userEmailTemplates, setUserEmailTemplates] = useState<any[]>([]);

  // Reference document state
  const [showReferencePanel, setShowReferencePanel] = useState(false);
  const [selectedReferenceDocs, setSelectedReferenceDocs] = useState<Array<{
    id: string;
    doc: any;
    type: 'patent' | 'publication' | 'upload' | 'foreign_patent' | 'mpep' | 'cfr' | 'usc';
  }>>([]);
  const [initialDocumentDismissed, setInitialDocumentDismissed] = useState(false);

  // Load all documents state
  const [allPatentDocs, setAllPatentDocs] = useState<any[]>([]);
  const [loadingPatents, setLoadingPatents] = useState(false);
  const [patientsLoaded, setPatientsLoaded] = useState(false);

  const [allPublicationDocs, setAllPublicationDocs] = useState<any[]>([]);
  const [loadingPublications, setLoadingPublications] = useState(false);
  const [publicationsLoaded, setPublicationsLoaded] = useState(false);

  const [allForeignPatentDocs, setAllForeignPatentDocs] = useState<any[]>([]);
  const [loadingForeignPatents, setLoadingForeignPatents] = useState(false);
  const [foreignPatientsLoaded, setForeignPatientsLoaded] = useState(false);

  const [allUploadDocs, setAllUploadDocs] = useState<any[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [uploadsLoaded, setUploadsLoaded] = useState(false);

  const [allDrafts, setAllDrafts] = useState<any[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [draftsLoaded, setDraftsLoaded] = useState(false);

  // State for viewing/editing a draft within examination page
  const [openedDraft, setOpenedDraft] = useState<any | null>(null);
  const [draftContent, setDraftContent] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);

  // Load functions for each document type
  const loadAllPatents = async () => {
    setLoadingPatents(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const patents = documents.filter((doc: any) => doc.file_type === 'patent');
      setAllPatentDocs(patents);
      setPatientsLoaded(true);
    } catch (error) {
      console.error('Failed to load all patents:', error);
    } finally {
      setLoadingPatents(false);
    }
  };

  const loadAllPublications = async () => {
    setLoadingPublications(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const publications = documents.filter((doc: any) => doc.file_type === 'publication');
      setAllPublicationDocs(publications);
      setPublicationsLoaded(true);
    } catch (error) {
      console.error('Failed to load all publications:', error);
    } finally {
      setLoadingPublications(false);
    }
  };

  const loadAllForeignPatents = async () => {
    setLoadingForeignPatents(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const foreignPatents = documents.filter((doc: any) => doc.file_type === 'foreign_patent');
      setAllForeignPatentDocs(foreignPatents);
      setForeignPatientsLoaded(true);
    } catch (error) {
      console.error('Failed to load all foreign patents:', error);
    } finally {
      setLoadingForeignPatents(false);
    }
  };

  const loadAllUploads = async () => {
    setLoadingUploads(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const uploads = documents.filter((doc: any) => doc.file_type === 'upload');
      setAllUploadDocs(uploads);
      setUploadsLoaded(true);
    } catch (error) {
      console.error('Failed to load all uploads:', error);
    } finally {
      setLoadingUploads(false);
    }
  };

  const loadAllDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const response = await draftApi.listDrafts();
      console.log('[ExaminationPage] Loaded drafts:', response.drafts);

      // Normalize the drafts to ensure title and content are strings
      const normalizedDrafts = (response.drafts || []).map((draft: any) => ({
        ...draft,
        title: typeof draft.title === 'string' ? draft.title : (draft.title?.title || 'Untitled Draft'),
        content: typeof draft.content === 'string' ? draft.content : (draft.content?.content || '')
      }));

      setAllDrafts(normalizedDrafts);
      setDraftsLoaded(true);
    } catch (error) {
      console.error('Failed to load all drafts:', error);
    } finally {
      setLoadingDrafts(false);
    }
  };

  // Handle opening a draft within examination page
  const handleOpenDraftInExamination = async (draft: any) => {
    try {
      console.log('[ExaminationPage] Opening draft locally:', draft);

      // Normalize title and content from the draft object
      const initialTitle = typeof draft.title === 'string' ? draft.title : (draft.title?.title || 'Loading...');
      const initialContent = typeof draft.content === 'string' ? draft.content : (draft.content?.content || '');

      // Set loading state
      setOpenedDraft(draft);
      setDraftContent(initialContent);
      setDraftTitle(initialTitle);
      setShowDraft(true);

      // Fetch full draft data if needed
      const fullDraft = await draftApi.getDraft(draft.draft_id);
      console.log('[ExaminationPage] Full draft loaded:', fullDraft);

      // Normalize the full draft data
      const normalizedTitle = typeof fullDraft.title === 'string' ? fullDraft.title : (fullDraft.title?.title || 'Untitled');
      const normalizedContent = typeof fullDraft.content === 'string' ? fullDraft.content : (fullDraft.content?.content || '');

      setOpenedDraft(fullDraft);
      setDraftContent(normalizedContent);
      setDraftTitle(normalizedTitle);

      addNotification({
        type: 'success',
        title: 'Draft Opened',
        message: `"${normalizedTitle}" is now open in examination view`,
        duration: 2000
      });
    } catch (error) {
      console.error('Failed to open draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load draft',
        duration: 3000
      });
      setShowDraft(false);
    }
  };

  // Handle saving draft changes
  const handleSaveDraft = async () => {
    if (!openedDraft) return;

    setSavingDraft(true);
    try {
      // Ensure title and content are strings before saving
      const normalizedTitle = typeof draftTitle === 'string' ? draftTitle : String(draftTitle || 'Untitled');
      const normalizedContent = typeof draftContent === 'string' ? draftContent : String(draftContent || '');

      console.log('[ExaminationPage] Saving draft:', {
        draft_id: openedDraft.draft_id,
        title: normalizedTitle,
        content: normalizedContent.substring(0, 100) + '...'
      });

      const response = await draftApi.updateDraft(openedDraft.draft_id, normalizedTitle, normalizedContent);
      console.log('[ExaminationPage] Save response:', response);

      // Update the draft in allDrafts list
      setAllDrafts(prevDrafts =>
        prevDrafts.map(d =>
          d.draft_id === openedDraft.draft_id
            ? { ...d, content: normalizedContent, title: normalizedTitle }
            : d
        )
      );

      // Update opened draft
      setOpenedDraft({ ...openedDraft, content: normalizedContent, title: normalizedTitle });

      // Also update the local state
      setDraftTitle(normalizedTitle);
      setDraftContent(normalizedContent);

      addNotification({
        type: 'success',
        title: 'Draft Saved',
        message: `"${normalizedTitle}" has been saved`,
        duration: 2000
      });

      // Dispatch event to refresh drafts in other components
      window.dispatchEvent(new Event('drafts-updated'));
    } catch (error) {
      console.error('Failed to save draft:', error);
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save draft changes',
        duration: 3000
      });
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle exporting draft to DOCX
  const handleExportDraft = async () => {
    if (!openedDraft) return;

    try {
      await exportDraftToDocx({
        title: draftTitle,
        content: draftContent,
        draftType: openedDraft.draft_type,
        metadata: {
          createdAt: openedDraft.created_at,
          updatedAt: openedDraft.updated_at,
        },
      });

      addNotification({
        type: 'success',
        title: 'Exported',
        message: 'Draft exported to DOCX successfully',
        duration: 2000
      });
    } catch (error) {
      console.error('Failed to export draft:', error);
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export draft to DOCX',
        duration: 3000
      });
    }
  };

  // MPEP browsing state
  const [mpepSectionInput, setMpepSectionInput] = useState('');
  const [mpepStructure, setMpepStructure] = useState<any>(null);
  const [loadingMpepStructure, setLoadingMpepStructure] = useState(false);
  const [loadingMpepSection, setLoadingMpepSection] = useState(false);
  const [expandedMpepChapters, setExpandedMpepChapters] = useState<Set<string>>(new Set());

  // CFR browsing state
  const [cfrSectionInput, setCfrSectionInput] = useState('');
  const [cfrStructure, setCfrStructure] = useState<any>(null);
  const [loadingCfrStructure, setLoadingCfrStructure] = useState(false);
  const [loadingCfrSection, setLoadingCfrSection] = useState(false);
  const [expandedCfrParts, setExpandedCfrParts] = useState<Record<string, boolean>>({});
  const [expandedCfrSubparts, setExpandedCfrSubparts] = useState<Record<string, boolean>>({});

  // Four-area document viewing system (OA, Pros Doc, Reference, Task Result, Draft)
  const [showOfficeAction, setShowOfficeAction] = useState(false);
  const [showProsDoc, setShowProsDoc] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const [showTaskResultViewer, setShowTaskResultViewer] = useState(false);
  const [showDraft, setShowDraft] = useState(false);

  // Viewing arrangement modal
  const [showArrangementModal, setShowArrangementModal] = useState(false);

  // Multi-document view arrangement (when 2+ docs selected)
  const [viewArrangement, setViewArrangement] = useState<'horizontal' | 'vertical' | 'grid'>('horizontal');
  const [splitPositions, setSplitPositions] = useState({ horizontal: 50, vertical: 50 });
  const [isResizingSplit, setIsResizingSplit] = useState(false);
  const [resizingSplitIndex, setResizingSplitIndex] = useState<number | null>(null);
  // Document panel widths for horizontal layout (percentages)
  const [docPanelWidths, setDocPanelWidths] = useState<number[]>([50, 50]);
  // Document panel heights for vertical layout (percentages)
  const [docPanelHeights, setDocPanelHeights] = useState<number[]>([50, 50]);
  // Grid layout splits
  const [gridColumnSplit, setGridColumnSplit] = useState(50); // percentage for left/right column
  const [gridLeftRowSplit, setGridLeftRowSplit] = useState(50); // percentage for top/bottom in left column
  const [gridRightRowSplit, setGridRightRowSplit] = useState(50); // percentage for top/bottom in right column
  const [isResizingGridColumn, setIsResizingGridColumn] = useState(false);
  const [isResizingGridLeftRow, setIsResizingGridLeftRow] = useState(false);
  const [isResizingGridRightRow, setIsResizingGridRightRow] = useState(false);

  // Task result view mode: 'result-only', 'pros-doc-only', 'split-view'
  const [taskResultViewMode, setTaskResultViewMode] = useState<'result-only' | 'pros-doc-only' | 'split-view'>('result-only');

  // Split view sub-mode: 'side-by-side' or 'stacked'
  const [splitViewMode, setSplitViewMode] = useState<'side-by-side' | 'stacked'>('side-by-side');

  // Vertical split resize (for stacked mode)
  const [verticalSplitPosition, setVerticalSplitPosition] = useState(50); // percentage
  const [isResizingVertical, setIsResizingVertical] = useState(false);

  // Horizontal split resize (for side-by-side mode in split view)
  const [horizontalSplitPosition, setHorizontalSplitPosition] = useState(50); // percentage
  const [isResizingHorizontalSplit, setIsResizingHorizontalSplit] = useState(false);

  // Task history state
  const [oaTaskHistory, setOaTaskHistory] = useState<any[]>([]);
  const [docTaskHistory, setDocTaskHistory] = useState<any[]>([]);
  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [showDocTaskHistory, setShowDocTaskHistory] = useState(false);
  const [loadingTaskHistory, setLoadingTaskHistory] = useState(false);
  const [loadingDocTaskHistory, setLoadingDocTaskHistory] = useState(false);

  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const setSessionApplications = useSessionStore(state => state.setSessionApplications);
  const sessionDocuments = useSessionStore(state => state.sessionDocuments);
  const { addNotification } = useNotificationStore();
  const startPolling = useJobPollingStore(state => state.startPolling);
  const { compactMode } = useCompactMode();

  // Add new application state
  const [newAppNumber, setNewAppNumber] = useState('');
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');
  const [onboardingElapsed, setOnboardingElapsed] = useState(0);
  const onboardingStartTime = useRef<number | null>(null);
  const onboardingAppId = useRef<string | null>(null);

  // Helper functions for managing reference documents
  const addReferenceDoc = useCallback((doc: any, type: 'patent' | 'publication' | 'upload' | 'foreign_patent' | 'mpep' | 'cfr' | 'usc') => {
    const id = `${type}-${doc.file_id || doc.docId || doc.publication_number || doc.patent_number || doc.foreign_patent_number || doc.section || Math.random()}`;

    // Check if this document is already selected
    const alreadySelected = selectedReferenceDocs.some(refDoc => refDoc.id === id);
    if (alreadySelected) {
      return; // Don't add duplicates
    }

    setSelectedReferenceDocs(prev => [...prev, { id, doc, type }]);
    setShowReference(true);
  }, [selectedReferenceDocs]);

  const removeReferenceDoc = useCallback((id: string) => {
    setSelectedReferenceDocs(prev => prev.filter(refDoc => refDoc.id !== id));
  }, []);

  const removeAllReferenceDocs = useCallback(() => {
    setSelectedReferenceDocs([]);
    setShowReference(false);
  }, []);

  // Load session context for active documents
  const [sessionContext, setSessionContext] = useState<any>(null);

  useEffect(() => {
    const loadSessionContext = () => {
      const storedData = localStorage.getItem('session_context');
      if (storedData && storedData !== "undefined") {
        try {
          const parsedData = JSON.parse(storedData);
          setSessionContext(parsedData);
        } catch (error) {
          console.error('Failed to parse session context:', error);
          setSessionContext(null);
        }
      } else {
        setSessionContext(null);
      }
    };

    loadSessionContext();

    // Listen for context updates
    const handleStorageUpdate = () => loadSessionContext();
    window.addEventListener('storage-update', handleStorageUpdate);
    window.addEventListener('storage', loadSessionContext);

    return () => {
      window.removeEventListener('storage-update', handleStorageUpdate);
      window.removeEventListener('storage', loadSessionContext);
    };
  }, []);

  // Listen for draft updates from other components
  useEffect(() => {
    const handleDraftsUpdated = () => {
      if (draftsLoaded) {
        loadAllDrafts();
      }
    };

    window.addEventListener('drafts-updated', handleDraftsUpdated);
    return () => window.removeEventListener('drafts-updated', handleDraftsUpdated);
  }, [draftsLoaded]);

  // Load user email templates
  useEffect(() => {
    const loadUserTemplates = async () => {
      try {
        const result = await taskApi.fetchEmailTemplates();
        setUserEmailTemplates(result.templates || []);
      } catch (error) {
        console.error('Failed to load user email templates:', error);
        setUserEmailTemplates([]);
      }
    };
    loadUserTemplates();
  }, []);

  // Track if we're in the middle of restoring state (to prevent premature saves)
  const [isRestoringState, setIsRestoringState] = useState(false);

  // Load saved examination state when component mounts (only for reference doc and flags)
  useEffect(() => {
    const stateKey = `examination-state-${selectedApplicationNumber || 'default'}`;
    const savedState = localStorage.getItem(stateKey);

    console.log('[ExaminationPage] LOADING state on mount:', {
      key: stateKey,
      appNumber: selectedApplicationNumber,
      hasSavedState: !!savedState,
      savedState: savedState,
    });

    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        console.log('[ExaminationPage] Restoring saved state:', parsed);

        // Set flag to prevent saving during restoration
        setIsRestoringState(true);

        // Restore Reference Docs (prosecution docs are restored in fetchProsecutionDocs)
        if (parsed.selectedReferenceDocs !== undefined && Array.isArray(parsed.selectedReferenceDocs)) {
          setSelectedReferenceDocs(parsed.selectedReferenceDocs);
        }
        if (parsed.showReference !== undefined) {
          setShowReference(parsed.showReference);
        }

        // Restore dismissed flag
        if (parsed.initialDocumentDismissed !== undefined) {
          setInitialDocumentDismissed(parsed.initialDocumentDismissed);
        }

        // Note: Don't restore showOfficeAction/showProsDoc here to avoid triggering saves
        // They will be restored in fetchProsecutionDocs after the docs are loaded
      } catch (error) {
        console.error('[ExaminationPage] Failed to parse saved state:', error);
      }
    } else {
      console.log('[ExaminationPage] No saved state found for key:', stateKey);
    }
  }, [selectedApplicationNumber]);

  // Handle initial document from Documents page (only if no reference docs are selected)
  useEffect(() => {
    if (initialDocument && selectedReferenceDocs.length === 0 && !initialDocumentDismissed) {
      console.log('[ExaminationPage] Setting initial document as reference:', initialDocument);

      // Determine document type
      const docType = (initialDocument.type || initialDocument.file_type || '').toString().toLowerCase();
      let type: 'patent' | 'publication' | 'upload' | 'foreign_patent' | 'mpep' | 'cfr' | 'usc' = 'upload';

      if (docType === 'patent') {
        type = 'patent';
      } else if (docType === 'publication') {
        type = 'publication';
      } else if (docType === 'upload') {
        type = 'upload';
      } else if (docType === 'foreign_patent') {
        type = 'foreign_patent';
      } else if (docType === 'mpep') {
        type = 'mpep';
      } else if (docType === 'cfr') {
        type = 'cfr';
      } else if (docType === 'usc') {
        type = 'usc';
      }

      addReferenceDoc(initialDocument, type);
    }
  }, [initialDocument, selectedReferenceDocs, initialDocumentDismissed, addReferenceDoc]);

  // Save examination state when documents change
  useEffect(() => {
    // Don't save if we don't have a valid application number yet
    if (!selectedApplicationNumber) {
      console.log('[ExaminationPage] Skipping save - no application number yet');
      return;
    }

    // Don't save if we're in the middle of restoring state
    if (isRestoringState) {
      console.log('[ExaminationPage] Skipping save - restoration in progress');
      return;
    }

    const stateKey = `examination-state-${selectedApplicationNumber}`;
    const stateToSave = {
      // Save only document IDs for prosecution docs (to restore after fetch)
      selectedOfficeActionDocId: selectedOfficeActionDoc?.docId || null,
      showOfficeAction,
      selectedProsDocId: selectedProsDoc?.docId || null,
      showProsDoc,
      // Reference docs can be saved fully as they come from elsewhere
      selectedReferenceDocs,
      showReference,
      initialDocumentDismissed,
    };

    console.log('[ExaminationPage] SAVING state to localStorage:', {
      key: stateKey,
      state: stateToSave,
      oaDocId: selectedOfficeActionDoc?.docId,
      prosDocId: selectedProsDoc?.docId,
      numReferenceDocs: selectedReferenceDocs.length,
    });

    localStorage.setItem(stateKey, JSON.stringify(stateToSave));
  }, [
    selectedApplicationNumber,
    selectedOfficeActionDoc,
    showOfficeAction,
    selectedProsDoc,
    showProsDoc,
    selectedReferenceDocs,
    showReference,
    initialDocumentDismissed,
    isRestoringState,
  ]);

  // Handle horizontal panel resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle vertical panel resizing (for stacked mode)
  const handleVerticalMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingVertical(true);
  };

  // Handle horizontal split resizing (for side-by-side mode)
  const handleHorizontalSplitMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingHorizontalSplit(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Handle left panel resizing
      if (isResizingLeftPanel) {
        const container = document.getElementById('examination-container');
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const mouseX = e.clientX - containerRect.left;

        // Constrain between 200px and 600px
        const newWidth = Math.min(Math.max(mouseX, 200), 600);
        setLeftPanelWidth(newWidth);
      }

      if (isResizing) {
        const container = document.getElementById('examination-container');
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const currentLeftPanelWidth = leftPanelCollapsed ? 60 : leftPanelWidth; // pixels
        const availableWidth = containerRect.width - currentLeftPanelWidth;
        const mouseX = e.clientX - containerRect.left - currentLeftPanelWidth;

        // Calculate percentage of available width
        const percentage = (mouseX / availableWidth) * 100;

        // Constrain between 20% and 80%
        const constrainedPercentage = Math.min(Math.max(percentage, 20), 80);
        setMiddlePanelWidth(constrainedPercentage);
      }

      if (isResizingVertical) {
        const rightPanel = document.getElementById('right-panel-content');
        if (!rightPanel) return;

        const panelRect = rightPanel.getBoundingClientRect();
        const mouseY = e.clientY - panelRect.top;

        // Calculate percentage of panel height
        const percentage = (mouseY / panelRect.height) * 100;

        // Constrain between 20% and 80%
        const constrainedPercentage = Math.min(Math.max(percentage, 20), 80);
        setVerticalSplitPosition(constrainedPercentage);
      }

      if (isResizingHorizontalSplit) {
        const rightPanel = document.getElementById('right-panel-content');
        if (!rightPanel) return;

        const panelRect = rightPanel.getBoundingClientRect();
        const mouseX = e.clientX - panelRect.left;

        // Calculate percentage of panel width
        const percentage = (mouseX / panelRect.width) * 100;

        // Constrain between 20% and 80%
        const constrainedPercentage = Math.min(Math.max(percentage, 20), 80);
        setHorizontalSplitPosition(constrainedPercentage);
      }

      // Handle document split resizing
      if (isResizingSplit && resizingSplitIndex !== null) {
        const middlePanel = document.querySelector('.middle-panel-content');
        if (!middlePanel) return;

        const panelRect = middlePanel.getBoundingClientRect();

        if (viewArrangement === 'horizontal') {
          const mouseX = e.clientX - panelRect.left;
          const percentage = (mouseX / panelRect.width) * 100;

          // Update widths for the panels on either side of the resize handle
          const newWidths = [...docPanelWidths];
          const totalBeforeSplit = newWidths.slice(0, resizingSplitIndex + 1).reduce((a, b) => a + b, 0);
          const totalAfterSplit = newWidths.slice(resizingSplitIndex + 1).reduce((a, b) => a + b, 0);

          // Calculate new sizes
          const newBeforeSplit = Math.min(Math.max(percentage, 10), 90);
          const newAfterSplit = 100 - newBeforeSplit;

          // Distribute proportionally
          if (totalBeforeSplit > 0) {
            for (let i = 0; i <= resizingSplitIndex; i++) {
              newWidths[i] = (newWidths[i] / totalBeforeSplit) * newBeforeSplit;
            }
          }
          if (totalAfterSplit > 0) {
            for (let i = resizingSplitIndex + 1; i < newWidths.length; i++) {
              newWidths[i] = (newWidths[i] / totalAfterSplit) * newAfterSplit;
            }
          }

          setDocPanelWidths(newWidths);
        } else if (viewArrangement === 'vertical') {
          const mouseY = e.clientY - panelRect.top;
          const percentage = (mouseY / panelRect.height) * 100;

          // Update heights for the panels on either side of the resize handle
          const newHeights = [...docPanelHeights];
          const totalBeforeSplit = newHeights.slice(0, resizingSplitIndex + 1).reduce((a, b) => a + b, 0);
          const totalAfterSplit = newHeights.slice(resizingSplitIndex + 1).reduce((a, b) => a + b, 0);

          // Calculate new sizes
          const newBeforeSplit = Math.min(Math.max(percentage, 10), 90);
          const newAfterSplit = 100 - newBeforeSplit;

          // Distribute proportionally
          if (totalBeforeSplit > 0) {
            for (let i = 0; i <= resizingSplitIndex; i++) {
              newHeights[i] = (newHeights[i] / totalBeforeSplit) * newBeforeSplit;
            }
          }
          if (totalAfterSplit > 0) {
            for (let i = resizingSplitIndex + 1; i < newHeights.length; i++) {
              newHeights[i] = (newHeights[i] / totalAfterSplit) * newAfterSplit;
            }
          }

          setDocPanelHeights(newHeights);
        }
      }

      // Handle grid resize for column split
      if (isResizingGridColumn) {
        const middlePanel = document.querySelector('.middle-panel-content');
        if (!middlePanel) return;

        const panelRect = middlePanel.getBoundingClientRect();
        const mouseX = e.clientX - panelRect.left;
        const percentage = (mouseX / panelRect.width) * 100;
        const constrainedPercentage = Math.min(Math.max(percentage, 20), 80);
        setGridColumnSplit(constrainedPercentage);
      }

      // Handle grid resize for left column row split
      if (isResizingGridLeftRow) {
        const middlePanel = document.querySelector('.middle-panel-content');
        if (!middlePanel) return;

        const panelRect = middlePanel.getBoundingClientRect();
        const mouseY = e.clientY - panelRect.top;
        const percentage = (mouseY / panelRect.height) * 100;
        const constrainedPercentage = Math.min(Math.max(percentage, 20), 80);
        setGridLeftRowSplit(constrainedPercentage);
      }

      // Handle grid resize for right column row split
      if (isResizingGridRightRow) {
        const middlePanel = document.querySelector('.middle-panel-content');
        if (!middlePanel) return;

        const panelRect = middlePanel.getBoundingClientRect();
        const mouseY = e.clientY - panelRect.top;
        const percentage = (mouseY / panelRect.height) * 100;
        const constrainedPercentage = Math.min(Math.max(percentage, 20), 80);
        setGridRightRowSplit(constrainedPercentage);
      }

    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsResizingVertical(false);
      setIsResizingHorizontalSplit(false);
      setIsResizingSplit(false);
      setResizingSplitIndex(null);
      setIsResizingGridColumn(false);
      setIsResizingGridLeftRow(false);
      setIsResizingGridRightRow(false);
      setIsResizingLeftPanel(false);
    };

    if (isResizing || isResizingVertical || isResizingHorizontalSplit || isResizingSplit || isResizingGridColumn || isResizingGridLeftRow || isResizingGridRightRow || isResizingLeftPanel) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isResizingVertical, isResizingHorizontalSplit, isResizingSplit, resizingSplitIndex, isResizingGridColumn, isResizingGridLeftRow, isResizingGridRightRow, isResizingLeftPanel, leftPanelCollapsed, leftPanelWidth, viewArrangement, docPanelWidths, docPanelHeights, gridColumnSplit, gridLeftRowSplit, gridRightRowSplit]);

  const renderAddNewApplication = () => (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <Plus className="w-4 h-4" />
        Add New Application
      </h4>
      <div className="flex gap-2">
        <Input
          type="text"
          placeholder="e.g., 16/123,456 or 16123456"
          value={newAppNumber}
          onChange={(e) => { setNewAppNumber(e.target.value); setOnboardingError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !isOnboarding) handleAddNewApplication(); }}
          disabled={isOnboarding}
          className="flex-1"
        />
        <Button
          onClick={handleAddNewApplication}
          disabled={isOnboarding || !newAppNumber.trim()}
          size="sm"
        >
          {isOnboarding ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              {Math.floor(onboardingElapsed / 60)}:{String(onboardingElapsed % 60).padStart(2, '0')}
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </>
          )}
        </Button>
      </div>
      {isOnboarding && (
        <div className="flex items-center gap-2 mt-2 text-xs text-blue-600">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Loading application... Elapsed: {Math.floor(onboardingElapsed / 60)}:{String(onboardingElapsed % 60).padStart(2, '0')}</span>
        </div>
      )}
      {onboardingError && (
        <p className="text-xs text-red-600 mt-1">{onboardingError}</p>
      )}
    </div>
  );

  // Filter applications based on search term
  const filteredApplications = useMemo(() => {
    if (!Array.isArray(sessionApplications) || sessionApplications.length === 0) {
      return [];
    }

    return sessionApplications.filter((app: any) => {
      const appData = app.applicationMetaData || app.application?.applicationMetaData;
      const title = appData?.inventionTitle || '';

      // Exclude applications without a title
      if (!title || title.trim() === '') {
        return false;
      }

      // If no search term, include all applications with titles
      if (!applicationSearchTerm) return true;

      const searchLower = applicationSearchTerm.toLowerCase();
      const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
      const patentNumber = appData?.patentNumber || '';
      const publicationNumber = appData?.earliestPublicationNumber || '';

      return (
        appId?.toLowerCase().includes(searchLower) ||
        title.toLowerCase().includes(searchLower) ||
        patentNumber.toLowerCase().includes(searchLower) ||
        publicationNumber.toLowerCase().includes(searchLower)
      );
    });
  }, [sessionApplications, applicationSearchTerm]);

  // Get selected application data
  const selectedApp: any = useMemo(() => {
    if (!selectedApplicationNumber || !sessionApplications) return null;

    return sessionApplications.find((app: any) => {
      const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
      return appId === selectedApplicationNumber;
    });
  }, [selectedApplicationNumber, sessionApplications]);

  const selectedAppData = selectedApp?.applicationMetaData || selectedApp?.application?.applicationMetaData;

  const handleSelectApplication = useCallback((appId: string) => {
    setSelectedApplicationNumber(appId);
    setShowApplicationSelector(false);
    setApplicationSearchTerm('');

    // Add to session context
    const storedContext = localStorage.getItem('session_context');
    const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};

    const currentApps = context.current_applications || [];
    if (!currentApps.includes(appId)) {
      context.current_applications = [...currentApps, appId];
      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));
    }

    addNotification({
      type: 'success',
      title: 'Application Selected',
      message: `Application ${appId} selected for examination`,
    });
  }, [addNotification]);

  const handleRefreshApplication = async () => {
    if (!selectedApplicationNumber) return;

    setIsRefreshingApp(true);

    try {
      addNotification({
        type: 'info',
        title: 'Refreshing Application',
        message: `Fetching latest data for ${selectedApplicationNumber} from USPTO...`,
        duration: 3000,
      });

      // Refresh the specific application from backend
      await patentApi.refreshApplication(selectedApplicationNumber);

      // Clear localStorage cache for this application
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes(`pros_info-${selectedApplicationNumber}`) ||
          key.includes(`pros_doc-${selectedApplicationNumber}`) ||
          key.includes(`app_info-${selectedApplicationNumber}`)
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // After refresh, reload session applications to get updated data
      const freshApplications = await patentApi.getSessionApplications(true);
      setSessionApplications(freshApplications);

      // Update session_applications in localStorage
      localStorage.setItem('session_applications', JSON.stringify(freshApplications));

      // Trigger storage update event to refresh other components
      window.dispatchEvent(new Event('storage-update'));

      // Reload prosecution documents to get fresh data
      await fetchProsecutionDocs(selectedApplicationNumber);

      addNotification({
        type: 'success',
        title: 'Refresh Complete',
        message: `${selectedApplicationNumber} has been updated with latest USPTO data.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to refresh application:', error);
      addNotification({
        type: 'error',
        title: 'Refresh Failed',
        message: `Failed to refresh ${selectedApplicationNumber}. Please try again.`,
        duration: 5000,
      });
    } finally {
      setIsRefreshingApp(false);
    }
  };

  // Elapsed time timer for application onboarding
  useEffect(() => {
    if (isOnboarding) {
      onboardingStartTime.current = Date.now();
      setOnboardingElapsed(0);
      const interval = setInterval(() => {
        if (onboardingStartTime.current) {
          setOnboardingElapsed(Math.floor((Date.now() - onboardingStartTime.current) / 1000));
        }
      }, 1000);
      return () => clearInterval(interval);
    } else {
      onboardingStartTime.current = null;
    }
  }, [isOnboarding]);

  // Listen for job-complete events to detect when onboarding finishes
  useEffect(() => {
    const handleJobComplete = async (event: CustomEvent) => {
      const { docType, docId, success } = event.detail;
      if (docType === 'application' && docId === onboardingAppId.current) {
        setIsOnboarding(false);
        if (success) {
          // Refresh session applications
          try {
            const apps = await patentApi.getSessionApplications(true);
            setSessionApplications(apps);
            localStorage.setItem('session_applications', JSON.stringify(apps));
          } catch (err) {
            console.error('Failed to refresh session applications after onboarding:', err);
          }
          // Auto-select the newly onboarded application
          handleSelectApplication(docId);
          setNewAppNumber('');
          setOnboardingError('');
        } else {
          setOnboardingError('Failed to load application. Please verify the number and try again.');
        }
        onboardingAppId.current = null;
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);
    return () => window.removeEventListener('job-complete', handleJobComplete as EventListener);
  }, [handleSelectApplication, setSessionApplications]);

  const handleAddNewApplication = async () => {
    setOnboardingError('');

    // Clean the application number
    let cleanedId = newAppNumber
      .trim()
      .replace(/,/g, '')
      .replace(/\//g, '')
      .replace(/^US/i, '')
      .replace(/[A-Z]\d*$/i, '');

    // Validate format (8 digits)
    if (!/^\d{8}$/.test(cleanedId)) {
      setOnboardingError('Application number must be 8 digits (e.g., 16123456 or 16/123,456)');
      return;
    }

    // Check if already in session
    const alreadyLoaded = sessionApplications?.some((app: any) => {
      const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
      return appId === cleanedId;
    });
    if (alreadyLoaded) {
      handleSelectApplication(cleanedId);
      setNewAppNumber('');
      return;
    }

    setIsOnboarding(true);
    onboardingAppId.current = cleanedId;

    try {
      const response = await documentApi.fetchAndLoadDocument('application', cleanedId);

      if (response?.job_id) {
        // Add to session context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== 'undefined' ? JSON.parse(stored) : {};
        context.current_applications = [...(context.current_applications || []), cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addLoadingDocument(cleanedId, 'Application', cleanedId);
        startPolling(response.job_id, 'application', cleanedId);
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (error: any) {
      console.error(`Failed to onboard application ${cleanedId}:`, error);
      setIsOnboarding(false);
      onboardingAppId.current = null;
      setOnboardingError(error.message || 'Failed to load application. Please try again.');
    }
  };

  // Fetch prosecution documents when application is selected
  const fetchProsecutionDocs = async (appNumber: string) => {
    if (!appNumber) return;

    try {
      setLoadingProsecution(true);
      console.log('Fetching prosecution documents for:', appNumber);

      const result = await patentApi.fetchProsecutionDocInfo(appNumber);
      console.log('Prosecution doc info result:', result);

      let prosInfoArray: any[] = [];
      if (result.pros_info && Array.isArray(result.pros_info)) {
        prosInfoArray = result.pros_info;
      } else if (Array.isArray(result)) {
        prosInfoArray = result;
      }

      const docs: ProsecutionDoc[] = prosInfoArray
        .filter(doc => Array.isArray(doc.downloadOptionBag))
        .map(doc => {
          const pdf = doc.downloadOptionBag.find((d: any) => d.mimeTypeIdentifier === 'PDF');
          return {
            date: new Date(doc.officialDate).toLocaleDateString(),
            rawDate: new Date(doc.officialDate),
            pdfUrl: pdf?.downloadUrl || null,
            description: doc.documentCodeDescriptionText || doc.documentCode,
            documentCode: doc.documentCode,
            docId: doc.documentIdentifier
          };
        })
        .filter(doc => !!doc.pdfUrl)
        .sort((a, b) => b.rawDate.getTime() - a.rawDate.getTime());

      setProsecutionDocs(docs);

      // Filter office actions (including restrictions)
      const oaDocuments = docs.filter(doc => {
        const desc = doc.description.toLowerCase();
        const code = doc.documentCode;
        // Include office actions, rejections, allowances, notifications, and restriction requirements
        // Exclude applicant responses (codes starting with "A.")
        if (code && code.startsWith('A.')) {
          return false;
        }
        return /(office action|rejection|allowance|notification)/i.test(desc) || code === 'CTRS';
      });
      setOaDocs(oaDocuments);

      // Filter claims documents
      const claimsDocuments = docs.filter(doc => {
        const desc = doc.description.toLowerCase();
        return desc.includes("claims") || doc.documentCode === "CLM";
      });
      setClaimsDocs(claimsDocuments);

      // Filter spec documents (specification, drawings, etc.)
      const specDocuments = docs.filter(doc => {
        const desc = doc.description.toLowerCase();
        return desc.includes("specification") ||
               desc.includes("drawings") ||
               desc.includes("abstract") ||
               doc.documentCode === "SPEC" ||
               doc.documentCode === "DRAW" ||
               doc.documentCode === "REM";
      });
      setSpecDocs(specDocuments);

      // Restore saved selections after fetching docs
      const stateKey = `examination-state-${appNumber}`;
      const savedState = localStorage.getItem(stateKey);
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          console.log('[ExaminationPage] Restoring document selections:', parsed);

          // Restore Office Action selection
          if (parsed.selectedOfficeActionDocId) {
            const oaDoc = oaDocuments.find(doc => doc.docId === parsed.selectedOfficeActionDocId);
            if (oaDoc) {
              console.log('[ExaminationPage] Restoring Office Action:', oaDoc);
              setSelectedOfficeActionDoc(oaDoc);
              if (parsed.showOfficeAction) {
                setShowOfficeAction(true);
              }
            }
          }

          // Restore Pros Doc selection
          if (parsed.selectedProsDocId) {
            const prosDoc = docs.find(doc => doc.docId === parsed.selectedProsDocId);
            if (prosDoc) {
              console.log('[ExaminationPage] Restoring Pros Doc:', prosDoc);
              setSelectedProsDoc(prosDoc);
              if (parsed.showProsDoc) {
                setShowProsDoc(true);
              }
            }
          }

          // Clear restoration flag after a brief delay to ensure all state updates complete
          setTimeout(() => {
            console.log('[ExaminationPage] Restoration complete, enabling saves');
            setIsRestoringState(false);
          }, 100);
        } catch (error) {
          console.error('[ExaminationPage] Failed to restore selections:', error);
          setIsRestoringState(false);
        }
      } else {
        // No saved state, clear restoration flag
        setIsRestoringState(false);
      }

      setLoadingProsecution(false);
    } catch (err) {
      console.error("Error fetching prosecution documents:", err);
      setLoadingProsecution(false);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load prosecution documents',
      });
    }
  };

  // Handler for loading MPEP section
  const handleLoadMpepSection = async (section: string) => {
    if (!section || !section.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Section',
        message: 'Please enter a valid MPEP section number',
      });
      return;
    }

    try {
      setLoadingMpepSection(true);
      const content = await resourceApi.fetchMpepContent(section.trim());

      if (content && content.content) {
        // Create a document object for the MPEP section
        const mpepDoc = {
          resource_id: `mpep-${section.trim()}`,
          file_name: `MPEP ${section.trim()}`,
          file_type: 'mpep',
          content: content.content,
          contentType: 'html', // Mark as HTML for proper rendering
          title: content.title || `MPEP ${section.trim()}`,
          section: section.trim()
        };

        addReferenceDoc(mpepDoc, 'mpep');

        addNotification({
          type: 'success',
          title: 'MPEP Section Loaded',
          message: `MPEP ${section.trim()} loaded successfully`,
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Section Not Found',
          message: `Could not find MPEP section ${section.trim()}`,
        });
      }
    } catch (error) {
      console.error('Error loading MPEP section:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load MPEP section',
      });
    } finally {
      setLoadingMpepSection(false);
    }
  };

  // Toggle MPEP chapter expansion
  const toggleMpepChapter = (chapterId: string) => {
    const newExpanded = new Set(expandedMpepChapters);
    if (newExpanded.has(chapterId)) {
      newExpanded.delete(chapterId);
    } else {
      newExpanded.add(chapterId);
    }
    setExpandedMpepChapters(newExpanded);
  };

  // Handler for loading CFR section
  const handleLoadCfrSection = async (section: string) => {
    if (!section || !section.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Section',
        message: 'Please enter a valid CFR section number',
      });
      return;
    }

    try {
      setLoadingCfrSection(true);
      const content = await resourceApi.fetchCfrContent(section.trim());

      if (content && (content.content || content.cfr_xml_content)) {
        // Create a document object for the CFR section
        const cfrDoc = {
          resource_id: `cfr-${section.trim()}`,
          file_name: `37 CFR ${section.trim()}`,
          file_type: 'cfr',
          content: content.content || content.cfr_xml_content,
          contentType: 'html',
          title: content.title || `37 CFR ${section.trim()}`,
          section: section.trim()
        };

        addReferenceDoc(cfrDoc, 'cfr');

        addNotification({
          type: 'success',
          title: 'CFR Section Loaded',
          message: `37 CFR ${section.trim()} loaded successfully`,
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Section Not Found',
          message: `Could not find CFR section ${section.trim()}`,
        });
      }
    } catch (error) {
      console.error('Error loading CFR section:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load CFR section',
      });
    } finally {
      setLoadingCfrSection(false);
    }
  };

  // Toggle CFR part expansion
  const toggleCfrPart = (partKey: string) => {
    setExpandedCfrParts(prev => ({ ...prev, [partKey]: !prev[partKey] }));
  };

  // Toggle CFR subpart expansion
  const toggleCfrSubpart = (subpartKey: string) => {
    setExpandedCfrSubparts(prev => ({ ...prev, [subpartKey]: !prev[subpartKey] }));
  };

  // Fetch prosecution docs when application changes
  useEffect(() => {
    if (selectedApplicationNumber) {
      // Reset PDF URLs when fetching new docs
      setOaPdfUrl(null);
      setProsPdfUrl(null);

      // Fetch docs (selections will be restored inside fetchProsecutionDocs if saved state exists)
      fetchProsecutionDocs(selectedApplicationNumber);
    }
  }, [selectedApplicationNumber]);

  // Listen for application selection event from wizard
  useEffect(() => {
    const handleSelectApplicationEvent = (event: CustomEvent) => {
      const { applicationNumber } = event.detail;
      if (applicationNumber) {
        console.log('[ExaminationPage] Received select-application event:', applicationNumber);
        // Use the same logic as the Change button's handleSelectApplication
        handleSelectApplication(applicationNumber);
      }
    };

    window.addEventListener('select-examination-application', handleSelectApplicationEvent as EventListener);

    return () => {
      window.removeEventListener('select-examination-application', handleSelectApplicationEvent as EventListener);
    };
  }, [handleSelectApplication]);

  // Set initial application if provided from wizard (fallback)
  useEffect(() => {
    if (initialApplicationNumber) {
      console.log('[ExaminationPage] Setting initial application number:', initialApplicationNumber);
      // Use the same logic as the Change button
      handleSelectApplication(initialApplicationNumber);
    }
  }, [initialApplicationNumber]);

  // Auto-select first application
  useEffect(() => {
    if (!selectedApplicationNumber && sessionApplications && sessionApplications.length > 0) {
      const firstApp: any = sessionApplications[0];
      const appId = firstApp.applicationNumberText || firstApp.application_id || firstApp.application?.applicationNumberText;
      if (appId) {
        setSelectedApplicationNumber(appId);
      }
    }
  }, [selectedApplicationNumber, sessionApplications]);

  // Load Office Action PDF when selected
  useEffect(() => {
    if (!selectedOfficeActionDoc || !selectedApplicationNumber) return;

    let stopPolling: (() => void) | null = null;

    const loadOaPdf = async () => {
      try {
        console.log('[ExaminationPage] Loading OA PDF with params:', {
          applicationNumber: selectedApplicationNumber,
          docId: selectedOfficeActionDoc.docId,
          fullDoc: selectedOfficeActionDoc,
        });

        setLoadingOaPdf(true);
        setOaPdfUrl(null);
        setOaPdfStatus('Requesting PDF...');

        // Start the PDF generation job
        const response = await patentApi.getOfficeActionPdf(
          selectedApplicationNumber,
          selectedOfficeActionDoc.docId
        );

        if (response?.job_id) {
          setOaPdfStatus('Generating PDF...');

          // Start polling for the result
          stopPolling = taskApi.startGraduatedPolling(
            async () => {
              const result = await patentApi.getPdfJobStatus(response.job_id);

              // Check if job is complete
              if (result?.status && result.status.toLowerCase().startsWith('complete')) {
                return result;
              }

              // Return result if there's an error status (stops polling)
              if (result?.status && result.status.toLowerCase().includes('error')) {
                return result;
              }

              // Still processing, return null to continue polling
              return null;
            },
            async (result) => {
              // Handle the result when polling stops
              if (result?.status && result.status.toLowerCase().startsWith('complete')) {
                try {
                  setOaPdfStatus('Downloading PDF...');
                  // Download the PDF
                  const pdfUrl = await patentApi.downloadOfficeActionPdf(
                    selectedApplicationNumber,
                    selectedOfficeActionDoc.docId
                  );
                  setOaPdfUrl(pdfUrl);
                  setLoadingOaPdf(false);
                  setOaPdfStatus('');
                  addNotification({
                    type: 'success',
                    title: 'Office Action Loaded',
                    message: `${selectedOfficeActionDoc.description} is ready`,
                  });
                } catch (error) {
                  console.error('Error downloading OA PDF:', error);
                  setLoadingOaPdf(false);
                  setOaPdfStatus('');
                  addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to download Office Action PDF',
                  });
                }
              } else if (result?.status && result.status.toLowerCase().includes('error')) {
                setLoadingOaPdf(false);
                setOaPdfStatus('');
                addNotification({
                  type: 'error',
                  title: 'Error',
                  message: `Failed to load Office Action PDF: ${result.status}`,
                });
              }
            },
            (error) => {
              console.error('Error polling for OA PDF:', error);
              setLoadingOaPdf(false);
              setOaPdfStatus('');
              addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to load Office Action PDF',
              });
            },
            (attempts, elapsedTime) => {
              setOaPdfStatus(`Loading PDF... (${Math.floor(elapsedTime / 1000)}s)`);
            }
          );
        } else {
          setLoadingOaPdf(false);
          setOaPdfStatus('');
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'Failed to start PDF generation',
          });
        }
      } catch (error) {
        console.error('Error loading OA PDF:', error);
        setLoadingOaPdf(false);
        setOaPdfStatus('');
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load Office Action PDF',
        });
      }
    };

    loadOaPdf();

    // Cleanup function to stop polling if component unmounts or doc changes
    return () => {
      if (stopPolling) {
        stopPolling();
      }
    };
  }, [selectedOfficeActionDoc, selectedApplicationNumber]);

  // Calculate deadlines when office action is selected
  useEffect(() => {
    if (!selectedOfficeActionDoc) {
      setOaDeadlines(null);
      return;
    }

    try {
      // Only calculate deadlines for office actions from 2025 onwards
      const oaYear = selectedOfficeActionDoc.rawDate.getFullYear();
      if (oaYear < 2025) {
        setOaDeadlines(null);
        return;
      }

      const deadlines = calculateOADeadlines(selectedOfficeActionDoc.rawDate);
      setOaDeadlines(deadlines);
    } catch (error) {
      console.error('Error calculating OA deadlines:', error);
      setOaDeadlines(null);
    }
  }, [selectedOfficeActionDoc]);

  // Load Prosecution Document PDF when selected
  useEffect(() => {
    if (!selectedProsDoc || !selectedApplicationNumber) return;

    let stopPolling: (() => void) | null = null;

    const loadProsPdf = async () => {
      try {
        console.log('[ExaminationPage] Loading Pros Doc PDF with params:', {
          applicationNumber: selectedApplicationNumber,
          docId: selectedProsDoc.docId,
          fullDoc: selectedProsDoc,
        });

        setLoadingProsPdf(true);
        setProsPdfUrl(null);
        setProsPdfStatus('Requesting PDF...');

        // Start the PDF generation job
        const response = await patentApi.getProsecutionDocPdf(
          selectedApplicationNumber,
          selectedProsDoc.docId
        );

        if (response?.job_id) {
          setProsPdfStatus('Generating PDF...');

          // Start polling for the result
          stopPolling = taskApi.startGraduatedPolling(
            async () => {
              const result = await patentApi.getPdfJobStatus(response.job_id);

              // Check if job is complete
              if (result?.status && result.status.toLowerCase().startsWith('complete')) {
                return result;
              }

              // Return result if there's an error status (stops polling)
              if (result?.status && result.status.toLowerCase().includes('error')) {
                return result;
              }

              // Still processing, return null to continue polling
              return null;
            },
            async (result) => {
              // Handle the result when polling stops
              if (result?.status && result.status.toLowerCase().startsWith('complete')) {
                try {
                  setProsPdfStatus('Downloading PDF...');
                  // Download the PDF
                  const pdfUrl = await patentApi.downloadProsecutionDocPdf(
                    selectedApplicationNumber,
                    selectedProsDoc.docId
                  );
                  setProsPdfUrl(pdfUrl);
                  setLoadingProsPdf(false);
                  setProsPdfStatus('');
                  addNotification({
                    type: 'success',
                    title: 'Document Loaded',
                    message: `${selectedProsDoc.description} is ready`,
                  });
                } catch (error) {
                  console.error('Error downloading Pros PDF:', error);
                  setLoadingProsPdf(false);
                  setProsPdfStatus('');
                  addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to download prosecution document PDF',
                  });
                }
              } else if (result?.status && result.status.toLowerCase().includes('error')) {
                setLoadingProsPdf(false);
                setProsPdfStatus('');
                addNotification({
                  type: 'error',
                  title: 'Error',
                  message: `Failed to load document PDF: ${result.status}`,
                });
              }
            },
            (error) => {
              console.error('Error polling for Pros PDF:', error);
              setLoadingProsPdf(false);
              setProsPdfStatus('');
              addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to load document PDF',
              });
            },
            (attempts, elapsedTime) => {
              setProsPdfStatus(`Loading PDF... (${Math.floor(elapsedTime / 1000)}s)`);
            }
          );
        } else {
          setLoadingProsPdf(false);
          setProsPdfStatus('');
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'Failed to start PDF generation',
          });
        }
      } catch (error) {
        console.error('Error loading Pros PDF:', error);
        setLoadingProsPdf(false);
        setProsPdfStatus('');
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load document PDF',
        });
      }
    };

    loadProsPdf();

    // Cleanup function to stop polling if component unmounts or doc changes
    return () => {
      if (stopPolling) {
        stopPolling();
      }
    };
  }, [selectedProsDoc, selectedApplicationNumber]);

  // Auto-collapse left panel when viewing multiple documents
  useEffect(() => {
    const numDocsShown = [showOfficeAction, showProsDoc, showReference, showTaskResultViewer].filter(Boolean).length;
    if (numDocsShown >= 2) {
      setLeftPanelCollapsed(true);
    }
  }, [showOfficeAction, showProsDoc, showReference, showTaskResultViewer]);

  // Sync the three-area system with legacy states
  useEffect(() => {
    setShowOfficeAction(!!selectedOfficeActionDoc);
  }, [selectedOfficeActionDoc]);

  useEffect(() => {
    setShowProsDoc(!!selectedProsDoc);
  }, [selectedProsDoc]);

  useEffect(() => {
    setShowReference(selectedReferenceDocs.length > 0);
  }, [selectedReferenceDocs]);

  // Auto-show task result when it becomes available
  useEffect(() => {
    setShowTaskResultViewer(showTaskResult);
  }, [showTaskResult]);

  // Build a flat list of all documents to display
  const documentsToDisplay = useMemo(() => {
    const docs: Array<{ type: 'oa' | 'pros' | 'reference' | 'task' | 'draft'; data: any; id: string }> = [];

    if (showOfficeAction && selectedOfficeActionDoc) {
      docs.push({ type: 'oa', data: selectedOfficeActionDoc, id: 'oa' });
    }
    if (showProsDoc && selectedProsDoc) {
      docs.push({ type: 'pros', data: selectedProsDoc, id: 'pros' });
    }
    if (showReference) {
      selectedReferenceDocs.forEach((refDoc) => {
        docs.push({ type: 'reference', data: refDoc, id: refDoc.id });
      });
    }
    if (showTaskResultViewer && taskResult) {
      docs.push({ type: 'task', data: taskResult, id: 'task' });
    }
    if (showDraft && openedDraft) {
      docs.push({ type: 'draft', data: openedDraft, id: 'draft' });
    }

    return docs;
  }, [showOfficeAction, selectedOfficeActionDoc, showProsDoc, selectedProsDoc, showReference, selectedReferenceDocs, showTaskResultViewer, taskResult, showDraft, openedDraft]);

  // Calculate how many documents are currently shown
  const numDocsShown = documentsToDisplay.length;

  // Reset panel widths/heights when number of documents changes
  useEffect(() => {
    if (numDocsShown === 2) {
      setDocPanelWidths([50, 50]);
      setDocPanelHeights([50, 50]);
      // Switch out of grid mode if only 2 documents
      if (viewArrangement === 'grid') {
        setViewArrangement('horizontal');
      }
    } else if (numDocsShown === 3) {
      setDocPanelWidths([33.33, 33.33, 33.34]);
      setDocPanelHeights([33.33, 33.33, 33.34]);
    } else if (numDocsShown === 4) {
      setDocPanelWidths([25, 25, 25, 25]);
      setDocPanelHeights([25, 25, 25, 25]);
    } else if (numDocsShown === 1) {
      // Switch out of grid mode if only 1 document
      if (viewArrangement === 'grid') {
        setViewArrangement('horizontal');
      }
    }
  }, [numDocsShown, viewArrangement]);

  // Load MPEP structure when References panel opens
  useEffect(() => {
    const loadMpepStructure = async () => {
      if (showReferencePanel && !mpepStructure && !loadingMpepStructure) {
        setLoadingMpepStructure(true);
        try {
          const structure = await resourceApi.fetchMpepInfo();
          setMpepStructure(structure);
        } catch (error) {
          console.error('Failed to load MPEP structure:', error);
        } finally {
          setLoadingMpepStructure(false);
        }
      }
    };

    loadMpepStructure();
  }, [showReferencePanel, mpepStructure, loadingMpepStructure]);

  // Load CFR structure when References panel opens
  useEffect(() => {
    const loadCfrStructure = async () => {
      if (showReferencePanel && !cfrStructure && !loadingCfrStructure) {
        setLoadingCfrStructure(true);
        try {
          const response = await fetch('/cfr_info.json');
          const data = await response.json();
          setCfrStructure(data);
        } catch (error) {
          console.error('Failed to load CFR structure:', error);
        } finally {
          setLoadingCfrStructure(false);
        }
      }
    };

    loadCfrStructure();
  }, [showReferencePanel, cfrStructure, loadingCfrStructure]);

  // Load application-level task history (OA Timeline)
  const loadOATaskHistory = async () => {
    if (!selectedApplicationNumber) return;

    try {
      setLoadingTaskHistory(true);

      // Get history for application-level task types
      const timelineHistoryResponse: any = await taskApi.getTaskHistory('oa_timeline');

      // Use only timeline history
      const timelineHistory = timelineHistoryResponse?.history || [];
      const allHistory = [...timelineHistory];

      // Filter history by current application number
      const filteredHistory = allHistory.filter(item =>
        item.application_number === selectedApplicationNumber ||
        item.selected_number === selectedApplicationNumber
      );

      // Sort by timestamp (newest first)
      const sortedHistory = filteredHistory.sort((a, b) =>
        new Date(b.time_received || b.timestamp).getTime() - new Date(a.time_received || a.timestamp).getTime()
      );

      // Transform history items
      const transformedHistory = sortedHistory.map(item => ({
        id: item.analysis_id,
        task_type: item.task_type,
        task_name: OA_TASKS.find(t => t.task_type === item.task_type)?.name || 'Unknown Task',
        result: item.analysis || item.result,
        timestamp: new Date(item.time_received || item.timestamp),
        resource_type: 'application',
        application_number: item.application_number || item.selected_number
      }));

      setOaTaskHistory(transformedHistory);
      setShowTaskHistory(true);
    } catch (error) {
      console.error('Failed to load OA task history:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Load Task History',
        message: 'Could not retrieve application task history',
      });
    } finally {
      setLoadingTaskHistory(false);
    }
  };

  // Load document-level task history (OA Extract, Analyze, Summary)
  const loadDocTaskHistory = async () => {
    if (!selectedApplicationNumber) return;

    try {
      setLoadingDocTaskHistory(true);

      // Get history for all document-level task types
      const extractHistoryResponse: any = await taskApi.getTaskHistory('oa_extract');
      const analyzeHistoryResponse: any = await taskApi.getTaskHistory('oa_analyze');
      const summaryArtHistoryResponse: any = await taskApi.getTaskHistory('oa_summary_art');
      const reportingEmailHistoryResponse: any = await taskApi.getTaskHistory('oa_reporting_email');

      // Combine and sort history from all document-level task types
      const extractHistory = extractHistoryResponse?.history || [];
      const analyzeHistory = analyzeHistoryResponse?.history || [];
      const summaryArtHistory = summaryArtHistoryResponse?.history || [];
      const reportingEmailHistory = reportingEmailHistoryResponse?.history || [];
      const allHistory = [...extractHistory, ...analyzeHistory, ...summaryArtHistory, ...reportingEmailHistory];

      // Filter history by current application number using selected_number
      const filteredHistory = allHistory.filter(item => {
        if (item.selected_number) {
          // Extract app number from selected_number format like "14621302-J9PQM8FERXEAPX4"
          const appNoFromSelected = item.selected_number.split("-")[0];
          return appNoFromSelected === selectedApplicationNumber;
        }
        // Fallback to application_number field if selected_number not available
        return item.application_number === selectedApplicationNumber;
      });

      // Transform history items and mark current document
      const transformedHistory = filteredHistory.map(item => ({
        id: item.analysis_id,
        task_type: item.task_type,
        task_name: OA_TASKS.find(t => t.task_type === item.task_type)?.name || 'Unknown Task',
        result: item.analysis || item.result,
        timestamp: new Date(item.time_received || item.timestamp),
        resource_type: 'office_action',
        doc_id: item.doc_id || item.document_id,
        isCurrentDocument: selectedOfficeActionDoc && item.selected_number &&
                          item.selected_number.includes(selectedOfficeActionDoc.docId)
      }));

      // Sort history items: current document tasks first, then by timestamp
      const sortedHistory = transformedHistory.sort((a, b) => {
        // First, prioritize current document tasks
        if (a.isCurrentDocument && !b.isCurrentDocument) return -1;
        if (!a.isCurrentDocument && b.isCurrentDocument) return 1;
        // Then sort by timestamp (newest first)
        return b.timestamp.getTime() - a.timestamp.getTime();
      });

      setDocTaskHistory(sortedHistory);
      setShowDocTaskHistory(true);
    } catch (error) {
      console.error('Failed to load document task history:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Load Task History',
        message: 'Could not retrieve document task history',
      });
    } finally {
      setLoadingDocTaskHistory(false);
    }
  };

  // Handle running OA task
  const handleRunTask = async (task: OATask) => {
    // Check if this is an application-level task (OA Timeline)
    const isApplicationLevelTask = task.task_type === 'oa_timeline';

    if (!selectedApplicationNumber) {
      addNotification({
        type: 'error',
        title: 'No Application Selected',
        message: 'Please select an application first',
      });
      return;
    }

    if (!isApplicationLevelTask && !selectedOfficeActionDoc) {
      addNotification({
        type: 'error',
        title: 'No Office Action Selected',
        message: 'Please select an office action document first',
      });
      return;
    }

    try {
      setTaskProcessing(true);
      setTaskResult(null);
      setShowTaskResult(false);

      addNotification({
        type: 'info',
        title: 'Task Started',
        message: `Running ${task.name}...`,
      });

      let response;

      // Call the appropriate API based on task type
      switch (task.task_type) {
        case 'oa_extract':
          response = await patentApi.extractOAInfo(selectedOfficeActionDoc!.docId, selectedApplicationNumber);
          break;
        case 'oa_analyze':
          response = await patentApi.analyzeOAIssues(selectedOfficeActionDoc!.docId, selectedApplicationNumber);
          break;
        case 'oa_summary_art':
          response = await patentApi.summarizeOAPriorArt(selectedOfficeActionDoc!.docId, selectedApplicationNumber);
          break;
        case 'oa_timeline':
          response = await patentApi.buildOATimeline(selectedApplicationNumber);
          break;
        case 'oa_reporting_email':
          response = await taskApi.submitOAReportingEmailTask(selectedOfficeActionDoc!.docId, selectedApplicationNumber, selectedEmailTemplate);
          break;
        default:
          throw new Error(`Unknown task type: ${task.task_type}`);
      }

      if (response?.job_id) {
        // Poll for task result using getCustomAnalysisResult
        const stopPolling = taskApi.startGraduatedPolling(
          async () => {
            const result = await patentApi.getCustomAnalysisResult(response.job_id);
            console.log('Poll result:', result);

            // Check if task is complete by looking for analysis_id
            if (result && result.analysis_id) {
              console.log('Task completed successfully, result:', result);
              return result;
            }

            console.log('Task still processing, continuing polling...');
            return null;
          },
          async (result) => {
            let taskResult = result.analysis;

            // Handle structured results with nested output
            let parsedResult = taskResult;
            if (typeof taskResult === 'string') {
              try {
                let jsonString = taskResult;

                // Remove outer quotes if present
                if (jsonString.startsWith("'") && jsonString.endsWith("'")) {
                  jsonString = jsonString.slice(1, -1);
                }

                try {
                  parsedResult = JSON.parse(jsonString);
                } catch (firstError) {
                  // Try converting Python dict string to JSON
                  // Handle: single quotes -> double quotes, None -> null, True -> true, False -> false
                  let fixedJsonString = jsonString
                    // Replace Python None with JSON null
                    .replace(/\bNone\b/g, 'null')
                    // Replace Python True with JSON true
                    .replace(/\bTrue\b/g, 'true')
                    // Replace Python False with JSON false
                    .replace(/\bFalse\b/g, 'false')
                    // Replace single quotes with double quotes (for keys and string values)
                    // This regex tries to be smart about only replacing structural quotes
                    .replace(/'/g, '"');

                  try {
                    parsedResult = JSON.parse(fixedJsonString);
                  } catch (secondError) {
                    // If that fails, try a more aggressive cleanup
                    // Sometimes there are escaped quotes or other issues
                    try {
                      // Remove any control characters and try again
                      fixedJsonString = fixedJsonString.replace(/[\x00-\x1F\x7F]/g, '');
                      parsedResult = JSON.parse(fixedJsonString);
                    } catch (thirdError) {
                      console.warn('Failed to parse task result as JSON:', thirdError);
                      parsedResult = taskResult;
                    }
                  }
                }
              } catch (e) {
                console.warn('Failed to parse task result:', e);
                parsedResult = taskResult;
              }
            }

            // Extract output field if present
            if (typeof parsedResult === 'object' && parsedResult !== null && parsedResult.hasOwnProperty('output')) {
              taskResult = parsedResult.output;
            } else {
              taskResult = parsedResult;
            }

            const resultData = {
              id: response.job_id,
              task_type: task.task_type,
              task_name: task.name,
              result: taskResult,
              timestamp: new Date(),
              resource_type: isApplicationLevelTask ? 'application' : 'office_action',
              selected_number: isApplicationLevelTask ? selectedApplicationNumber : selectedOfficeActionDoc?.docId,
              application_number: selectedApplicationNumber
            };

            setTaskResult(resultData);
            setShowTaskResult(true);
            setTaskProcessing(false);

            addNotification({
              type: 'success',
              title: 'Task Completed',
              message: `${task.name} completed successfully`,
            });
          },
          (error) => {
            console.error('Error polling for task result:', error);
            setTaskProcessing(false);
            addNotification({
              type: 'error',
              title: 'Error',
              message: `${task.name} failed or timed out`,
            });
          }
        );
      } else {
        throw new Error('No job ID returned');
      }
    } catch (error) {
      console.error('Error running task:', error);
      setTaskProcessing(false);
      addNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to run ${task.name}`,
      });
    }
  };

  // Show empty state if no applications available
  if (!sessionApplications || sessionApplications.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full bg-gray-50">
          <Database className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Applications Available</h3>
          <p className="text-sm text-gray-500 text-center max-w-md mb-4">
            Add patent applications to your session to view examination insights.
            Use the chat to add applications by their application number.
          </p>
          <Button
            onClick={() => setShowApplicationSelector(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Select Application
          </Button>
        </div>

        {/* Application Selector Modal */}
        <Dialog open={showApplicationSelector} onOpenChange={setShowApplicationSelector}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Select Application</DialogTitle>
              <DialogDescription className="text-gray-600">
                Choose an application to examine
              </DialogDescription>
            </DialogHeader>

            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by application number, title, patent number, or publication number..."
                  value={applicationSearchTerm}
                  onChange={(e) => setApplicationSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredApplications.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {applicationSearchTerm ? 'No applications match your search.' : 'No applications available.'}
                </div>
              ) : (
                filteredApplications.map((app: any, index: number) => {
                  const appData = app.applicationMetaData || app.application?.applicationMetaData;
                  const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
                  const hasPatent = appData?.patentNumber;
                  const hasPublication = appData?.earliestPublicationNumber;

                  return (
                    <button
                      key={appId || index}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      onClick={() => handleSelectApplication(appId)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            Application: {appId}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {appData?.inventionTitle || 'No title available'}
                          </div>
                          <div className="flex gap-2 mt-2">
                            {hasPatent && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                Patent: {hasPatent}
                              </Badge>
                            )}
                            {hasPublication && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                Pub: {hasPublication}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            {renderAddNewApplication()}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (!selectedAppData) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full bg-gray-50">
          <FileText className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Application Selected</h3>
          <p className="text-sm text-gray-500 text-center max-w-md mb-4">
            Select an application to view examination details
          </p>
          <Button
            onClick={() => setShowApplicationSelector(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Select Application
          </Button>
        </div>

        {/* Application Selector Modal */}
        <Dialog open={showApplicationSelector} onOpenChange={setShowApplicationSelector}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Select Application</DialogTitle>
              <DialogDescription className="text-gray-600">
                Choose an application to examine
              </DialogDescription>
            </DialogHeader>

            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by application number, title, patent number, or publication number..."
                  value={applicationSearchTerm}
                  onChange={(e) => setApplicationSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredApplications.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {applicationSearchTerm ? 'No applications match your search.' : 'No applications available.'}
                </div>
              ) : (
                filteredApplications.map((app: any, index: number) => {
                  const appData = app.applicationMetaData || app.application?.applicationMetaData;
                  const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
                  const hasPatent = appData?.patentNumber;
                  const hasPublication = appData?.earliestPublicationNumber;

                  return (
                    <button
                      key={appId || index}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      onClick={() => handleSelectApplication(appId)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            Application: {appId}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {appData?.inventionTitle || 'No title available'}
                          </div>
                          <div className="flex gap-2 mt-2">
                            {hasPatent && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                Patent: {hasPatent}
                              </Badge>
                            )}
                            {hasPublication && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                Pub: {hasPublication}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            {renderAddNewApplication()}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div
      id="examination-container"
      className={`flex h-full bg-gray-50 relative ${isResizing || isResizingVertical || isResizingLeftPanel ? 'select-none' : ''}`}
    >
      {/* Left Panel - Application Details & Document Selection */}
      <div
        className="border-r border-gray-200 bg-white flex flex-col flex-shrink-0"
        style={{
          width: leftPanelCollapsed ? '60px' : `${leftPanelWidth}px`,
          transition: leftPanelCollapsed ? 'width 0.3s' : 'none'
        }}
      >
        {leftPanelCollapsed ? (
          // Collapsed view
          <div className="flex flex-col items-center h-full">
            <div className="p-2 border-b border-gray-200 w-full">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-10"
                onClick={() => setLeftPanelCollapsed(false)}
                title="Expand panel"
              >
                <ChevronDown className="w-4 h-4 rotate-90" />
              </Button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-start pt-4 gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="w-10 h-10 p-0 bg-purple-600 text-white hover:bg-purple-700 hover:text-white"
                onClick={() => setShowTaskPanel(true)}
                title="Open Analysis Tools"
              >
                <Gavel className="w-4 h-4" />
              </Button>
              {selectedApplicationNumber && (
                <div className="text-xs font-medium text-gray-600 mt-4" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                  {selectedApplicationNumber}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Expanded view
          <>
            {/* Header */}
            <div className={`border-b border-gray-200 ${compactMode ? 'p-2' : 'p-4'}`}>
              <div className={`flex items-center justify-between ${compactMode ? 'mb-2' : 'mb-3'}`}>
                <div className={`flex ${compactMode ? 'gap-1' : 'gap-2'}`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-1"
                    onClick={() => setLeftPanelCollapsed(true)}
                    title="Collapse panel"
                  >
                    <ChevronUp className="w-3 h-3 -rotate-90" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
                    onClick={() => setShowApplicationSelector(true)}
                  >
                    Change
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs bg-purple-600 text-white hover:bg-purple-700 hover:text-white"
                    onClick={() => setShowTaskPanel(true)}
                    title="Open Analysis Tools"
                  >
                    <Gavel className="w-3 h-3 mr-1" />
                    Analysis
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs bg-green-600 text-white hover:bg-green-700 hover:text-white"
                    onClick={handleRefreshApplication}
                    disabled={isRefreshingApp}
                    title="Refresh application data from USPTO"
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${isRefreshingApp ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              <h3 className="text-gray-900 leading-tight mb-3 text-sm font-medium">
                {selectedAppData?.inventionTitle || 'No title available'}
              </h3>

              <div className="space-y-2">
                <div className="flex items-start gap-2 text-xs">
                  <Hash className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-gray-500">APPLICATION NO.</div>
                    <div className="text-gray-900 font-medium">{selectedApplicationNumber}</div>
                  </div>
                </div>
              </div>
            </div>

            <ScrollArea className="flex-1 overflow-y-auto">
              <div className="p-4 space-y-4">
            {/* Show Details Section */}
            <Collapsible open={showDetails} onOpenChange={setShowDetails}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between h-9 text-sm">
                  <span>Application Details</span>
                  {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-3">
                <div className="space-y-2.5 text-xs">
                  {selectedAppData?.filingDate && (
                    <div className="flex items-start gap-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500 mb-0.5">Filing Date</div>
                        <div className="text-gray-900">{selectedAppData.filingDate}</div>
                      </div>
                    </div>
                  )}
                  {selectedAppData?.firstInventorName && (
                    <div className="flex items-start gap-2">
                      <User className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500 mb-0.5">Inventors</div>
                        <div className="text-gray-900">{selectedAppData.firstInventorName}</div>
                      </div>
                    </div>
                  )}
                  {selectedAppData?.assigneeName && (
                    <div className="flex items-start gap-2">
                      <Building2 className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500 mb-0.5">Assignee</div>
                        <div className="text-gray-900">{selectedAppData.assigneeName}</div>
                      </div>
                    </div>
                  )}
                  {selectedAppData?.examinerNameText && (
                    <div className="flex items-start gap-2">
                      <User className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500 mb-0.5">Examiner</div>
                        <div className="text-gray-900">{selectedAppData.examinerNameText}</div>
                      </div>
                    </div>
                  )}
                  {selectedAppData?.applicationStatusDescriptionText && (
                    <div className="flex items-start gap-2">
                      <FileText className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-gray-500 mb-0.5">Status</div>
                        <div className="text-gray-900">{selectedAppData.applicationStatusDescriptionText}</div>
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Office Action Response Deadlines */}
            {selectedOfficeActionDoc && oaDeadlines && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-blue-900">Response Deadlines</h3>
                </div>

                {/* Office Action Info */}
                <div className="mb-3 pb-3 border-b border-blue-200">
                  <div className="text-xs text-gray-700 font-medium mb-1">
                    {selectedOfficeActionDoc.description}
                  </div>
                  <div className="text-xs text-gray-500">
                    Mail date: {selectedOfficeActionDoc.date}
                  </div>
                </div>

                {/* 3-Month Deadline */}
                <div className="mb-3">
                  <div className="text-xs font-semibold text-blue-800 mb-1">3-Month:</div>
                  <div className="text-sm font-bold text-blue-900 mb-1">
                    {oaDeadlines.threeMonthDeadlineFormatted}
                  </div>
                  <div className="text-xs">
                    {(() => {
                      const daysLeft = daysUntilDeadline(oaDeadlines.threeMonthDeadline);
                      if (daysLeft < 0) {
                        return <span className="text-red-600 font-semibold">Past due by {Math.abs(daysLeft)} days</span>;
                      } else if (daysLeft === 0) {
                        return <span className="text-orange-600 font-semibold">Due today!</span>;
                      } else if (daysLeft <= 7) {
                        return <span className="text-orange-600 font-semibold">{daysLeft} days remaining</span>;
                      } else if (daysLeft <= 30) {
                        return <span className="text-yellow-700 font-semibold">{daysLeft} days remaining</span>;
                      } else {
                        return <span className="text-blue-600">{daysLeft} days remaining</span>;
                      }
                    })()}
                  </div>
                </div>

                <Separator className="bg-blue-200 mb-3" />

                {/* 6-Month Deadline */}
                <div>
                  <div className="text-xs font-semibold text-blue-800 mb-1">6-Month:</div>
                  <div className="text-sm font-bold text-blue-900 mb-1">
                    {oaDeadlines.sixMonthDeadlineFormatted}
                  </div>
                  <div className="text-xs">
                    {(() => {
                      const daysLeft = daysUntilDeadline(oaDeadlines.sixMonthDeadline);
                      if (daysLeft < 0) {
                        return <span className="text-red-600 font-semibold">Past due by {Math.abs(daysLeft)} days</span>;
                      } else if (daysLeft === 0) {
                        return <span className="text-orange-600 font-semibold">Due today!</span>;
                      } else if (daysLeft <= 7) {
                        return <span className="text-orange-600 font-semibold">{daysLeft} days remaining</span>;
                      } else if (daysLeft <= 30) {
                        return <span className="text-yellow-700 font-semibold">{daysLeft} days remaining</span>;
                      } else {
                        return <span className="text-blue-600">{daysLeft} days remaining</span>;
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}

              </div>
            </ScrollArea>
          </>
        )}
      </div>

      {/* Resize Handle for Left Panel */}
      {!leftPanelCollapsed && (
        <div
          className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-colors flex-shrink-0 relative group"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizingLeftPanel(true);
          }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
        </div>
      )}

      {/* Middle Panel - Multi-Document Viewer */}
      <div
        className="flex-1 flex flex-col max-w-[calc(100vw-66px)] bg-white"
      >
        {/* Header with Document Selectors */}
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Office Action Selector */}
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={selectedOfficeActionDoc ? "default" : "outline"}
                    size="sm"
                    className="h-8"
                  >
                    <span className="truncate max-w-[150px]">
                      {selectedOfficeActionDoc ? selectedOfficeActionDoc.description : 'Office Action'}
                    </span>
                    <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[400px] max-h-[400px] overflow-y-auto">
                  <DropdownMenuLabel>
                    Office Actions ({oaDocs.length})
                    {loadingProsecution && (
                      <Loader2 className="w-4 h-4 animate-spin inline ml-2" />
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {oaDocs.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500">
                      {loadingProsecution ? 'Loading documents...' : 'No office actions found'}
                    </div>
                  ) : (
                    oaDocs.map((doc) => (
                      <DropdownMenuItem
                        key={doc.docId}
                        onClick={() => setSelectedOfficeActionDoc(doc)}
                        className={`cursor-pointer ${
                          selectedOfficeActionDoc?.docId === doc.docId ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex flex-col gap-1 w-full">
                          <div className="font-medium text-sm">{doc.description}</div>
                          <div className="text-xs text-gray-500">{doc.date}</div>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {selectedOfficeActionDoc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => {
                    setSelectedOfficeActionDoc(null);
                    setOaPdfUrl(null);
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Pros Doc Selector */}
            <div className="flex items-center gap-2">
              <FileStack className="w-4 h-4 text-green-600" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={selectedProsDoc ? "default" : "outline"}
                    size="sm"
                    className="h-8"
                  >
                    <span className="truncate max-w-[150px]">
                      {selectedProsDoc ? selectedProsDoc.description : 'Pros Doc'}
                    </span>
                    <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[400px] max-h-[400px] overflow-y-auto">
                  <DropdownMenuLabel>
                    Prosecution Documents
                    {loadingProsecution && (
                      <Loader2 className="w-4 h-4 animate-spin inline ml-2" />
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {claimsDocs.length === 0 && specDocs.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500">
                      {loadingProsecution ? 'Loading documents...' : 'No prosecution documents found'}
                    </div>
                  ) : (
                    <>
                      {claimsDocs.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Claims</div>
                          {claimsDocs.map((doc) => (
                            <DropdownMenuItem
                              key={doc.docId}
                              onClick={() => setSelectedProsDoc(doc)}
                              className={`cursor-pointer ${
                                selectedProsDoc?.docId === doc.docId ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex flex-col gap-1 w-full">
                                <div className="font-medium text-sm">{doc.description}</div>
                                <div className="text-xs text-gray-500">{doc.date}</div>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}
                      {specDocs.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">Specifications</div>
                          {specDocs.map((doc) => (
                            <DropdownMenuItem
                              key={doc.docId}
                              onClick={() => setSelectedProsDoc(doc)}
                              className={`cursor-pointer ${
                                selectedProsDoc?.docId === doc.docId ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex flex-col gap-1 w-full">
                                <div className="font-medium text-sm">{doc.description}</div>
                                <div className="text-xs text-gray-500">{doc.date}</div>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {selectedProsDoc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setSelectedProsDoc(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Draft Selector */}
            <div className="flex items-center gap-2">
              <FileEdit className="w-4 h-4 text-purple-600" />
              <DropdownMenu onOpenChange={(open) => {
                if (open && !draftsLoaded) {
                  loadAllDrafts();
                }
              }}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={openedDraft ? "default" : "outline"}
                    size="sm"
                    className="h-8"
                  >
                    <span className="truncate max-w-[150px]">
                      {openedDraft ? openedDraft.title || 'Untitled Draft' : 'Draft'}
                    </span>
                    <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[400px] max-h-[400px] overflow-y-auto">
                  <DropdownMenuLabel>
                    Drafts ({allDrafts.length})
                    {loadingDrafts && (
                      <Loader2 className="w-4 h-4 animate-spin inline ml-2" />
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2"
                      onClick={async () => {
                        try {
                          const newDraft = await draftApi.createDraft('Untitled Draft', '', 'general');
                          setAllDrafts(prev => [newDraft, ...prev]);
                          setOpenedDraft(newDraft);
                          setDraftContent('');
                          setDraftTitle('Untitled Draft');
                          setShowDraft(true);
                          // Dispatch event to refresh drafts in other components
                          window.dispatchEvent(new Event('drafts-updated'));
                        } catch (error) {
                          console.error('Failed to create draft:', error);
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      New Draft
                    </Button>
                  </div>
                  <DropdownMenuSeparator />
                  {allDrafts.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500">
                      {loadingDrafts ? 'Loading drafts...' : 'No drafts found'}
                    </div>
                  ) : (
                    allDrafts.map((draft) => (
                      <DropdownMenuItem
                        key={draft.draft_id}
                        onClick={() => {
                          setOpenedDraft(draft);
                          setDraftContent(draft.content || '');
                          setDraftTitle(draft.title || '');
                          setShowDraft(true);
                        }}
                        className={`cursor-pointer ${
                          openedDraft?.draft_id === draft.draft_id ? 'bg-purple-50' : ''
                        }`}
                      >
                        <div className="flex flex-col gap-1 w-full">
                          <div className="font-medium text-sm">{draft.title || 'Untitled'}</div>
                          <div className="text-xs text-gray-500 line-clamp-2">
                            {draft.content ? draft.content.substring(0, 100) + (draft.content.length > 100 ? '...' : '') : 'Empty draft'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(draft.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {openedDraft && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => {
                    setOpenedDraft(null);
                    setDraftContent('');
                    setDraftTitle('');
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Reference Docs Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <BookOpen className="w-4 h-4 text-purple-600" />
              {selectedReferenceDocs.length === 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setShowReferencePanel(true)}
                >
                  <span className="truncate max-w-[150px]">Docs / References</span>
                </Button>
              ) : (
                <>
                  {selectedReferenceDocs.map((refDoc) => (
                    <div key={refDoc.id} className="flex items-center gap-1">
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8"
                        onClick={() => setShowReferencePanel(true)}
                      >
                        <span className="truncate max-w-[120px]">
                          {refDoc.doc.file_name || refDoc.doc.title || 'Reference'}
                        </span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => removeReferenceDoc(refDoc.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => setShowReferencePanel(true)}
                  >
                    + Add
                  </Button>
                </>
              )}
            </div>

            {/* Task Result Button */}
            {taskResult && (
              <div className="flex items-center gap-2">
                <Gavel className="w-4 h-4 text-purple-600" />
                <Button
                  variant={showTaskResultViewer ? "default" : "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => setShowTaskResultViewer(!showTaskResultViewer)}
                >
                  <span className="truncate max-w-[150px]">
                    {taskResult.task_name || 'Task Result'}
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => {
                    setTaskResult(null);
                    setShowTaskResult(false);
                    setShowTaskResultViewer(false);
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}

            {/* Arrangement Options Button - Only show when 2+ documents */}
            {numDocsShown >= 2 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 ml-auto"
                onClick={() => setShowArrangementModal(true)}
              >
                <List className="w-4 h-4 mr-2" />
                Layout
              </Button>
            )}
          </div>
        </div>

        {/* Document Viewing Area */}
        <div className="flex-1 overflow-auto bg-gray-100 middle-panel-content">
          {numDocsShown === 0 ? (
            // No documents selected
            <div className="flex flex-col items-center justify-center h-full">
              <FileText className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Selected</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                Select documents from the dropdowns above to view them here
              </p>
            </div>
          ) : numDocsShown === 1 ? (
            // Single document view
            <div className="h-full overflow-hidden">
              {showOfficeAction && selectedOfficeActionDoc && (
                loadingOaPdf ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                    <p className="text-sm text-gray-600">{oaPdfStatus || 'Loading PDF...'}</p>
                  </div>
                ) : oaPdfUrl ? (
                  <div className="flex flex-col items-center p-4 h-full overflow-auto">
                    <div className="mb-4 flex gap-2">
                      <Button size="sm" onClick={() => setOaScale(Math.max(0.5, oaScale - 0.1))}>
                        Zoom Out
                      </Button>
                      <Button size="sm" onClick={() => setOaScale(1.0)}>
                        Reset ({Math.round(oaScale * 100)}%)
                      </Button>
                      <Button size="sm" onClick={() => setOaScale(Math.min(2.0, oaScale + 0.1))}>
                        Zoom In
                      </Button>
                    </div>
                    <div className="w-full max-w-[900px]">
                      <Document
                        file={oaPdfUrl}
                        onLoadSuccess={({ numPages }) => {
                          setOaNumPages(numPages);
                          setLoadingOaPdf(false);
                        }}
                        onLoadError={(error) => {
                          console.error('Error loading OA PDF:', error);
                          setLoadingOaPdf(false);
                        }}
                        loading={
                          <div className="flex items-center justify-center p-8">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                          </div>
                        }
                      >
                        {Array.from(new Array(oaNumPages), (_, index) => (
                          <Page
                            key={`oa-page-${index + 1}`}
                            pageNumber={index + 1}
                            width={900 * oaScale}
                            className="mb-4 shadow-lg mx-auto"
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                          />
                        ))}
                      </Document>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full">
                    <FileText className="w-16 h-16 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No PDF Available</h3>
                    <p className="text-sm text-gray-500">This document does not have a PDF version</p>
                  </div>
                )
              )}

              {showProsDoc && selectedProsDoc && (
                loadingProsPdf ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                    <p className="text-sm text-gray-600">{prosPdfStatus || 'Loading PDF...'}</p>
                  </div>
                ) : prosPdfUrl ? (
                  <div className="flex flex-col items-center p-4 h-full overflow-auto">
                    <div className="mb-4 flex gap-2">
                      <Button size="sm" onClick={() => setProsScale(Math.max(0.5, prosScale - 0.1))}>
                        Zoom Out
                      </Button>
                      <Button size="sm" onClick={() => setProsScale(1.0)}>
                        Reset ({Math.round(prosScale * 100)}%)
                      </Button>
                      <Button size="sm" onClick={() => setProsScale(Math.min(2.0, prosScale + 0.1))}>
                        Zoom In
                      </Button>
                    </div>
                    <div className="w-full max-w-[900px]">
                      <Document
                        file={prosPdfUrl}
                        onLoadSuccess={({ numPages }) => {
                          setProsNumPages(numPages);
                          setLoadingProsPdf(false);
                        }}
                        onLoadError={(error) => {
                          console.error('Error loading Pros Doc PDF:', error);
                          setLoadingProsPdf(false);
                        }}
                        loading={
                          <div className="flex items-center justify-center p-8">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                          </div>
                        }
                      >
                        {Array.from(new Array(prosNumPages), (_, index) => (
                          <Page
                            key={`pros-page-${index + 1}`}
                            pageNumber={index + 1}
                            width={900 * prosScale}
                            className="mb-4 shadow-lg mx-auto"
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                          />
                        ))}
                      </Document>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full">
                    <FileText className="w-16 h-16 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No PDF Available</h3>
                    <p className="text-sm text-gray-500">This document does not have a PDF version</p>
                  </div>
                )
              )}

              {showReference && selectedReferenceDocs.length > 0 && (
                <>
                  {selectedReferenceDocs.map((refDoc) => (
                    <DocumentViewer
                      key={refDoc.id}
                      document={refDoc.doc}
                      onClose={() => removeReferenceDoc(refDoc.id)}
                      isFullWidth={true}
                    />
                  ))}
                </>
              )}

              {showTaskResultViewer && taskResult && (
                <div className="h-full overflow-auto p-4 bg-white">
                  {taskResult.task_type === 'oa_extract' && (
                    <OAExtractDisplay result={taskResult.result} />
                  )}
                  {taskResult.task_type === 'oa_analyze' && (
                    <OAAnalyzeDisplay result={taskResult.result} />
                  )}
                  {taskResult.task_type === 'oa_summary_art' && (
                    <OASummaryArtDisplay result={taskResult.result} />
                  )}
                  {taskResult.task_type === 'oa_timeline' && (
                    <OATimelineDisplay result={taskResult.result} />
                  )}
                  {taskResult.task_type === 'oa_reporting_email' && (
                    <OAEmailResultView data={taskResult.result} showOpenInEmail={false} />
                  )}
                </div>
              )}

              {showDraft && openedDraft && (
                <div className="h-full flex flex-col p-4 bg-white">
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <Input
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      className="flex-1 font-semibold text-lg"
                      placeholder="Draft title..."
                    />
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        {openedDraft.draft_type || 'general'}
                      </Badge>
                      <Button
                        onClick={handleExportDraft}
                        variant="outline"
                        size="sm"
                        className="gap-1"
                      >
                        <Download className="w-4 h-4" />
                        Export
                      </Button>
                      <Button
                        onClick={handleSaveDraft}
                        disabled={savingDraft}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        {savingDraft ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowDraft(false);
                          setOpenedDraft(null);
                          setDraftContent('');
                          setDraftTitle('');
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <LineNumberedTextarea
                      value={draftContent}
                      onChange={setDraftContent}
                      placeholder="Start typing your draft content here..."
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Multiple documents view with arrangement options
            <div className="h-full flex flex-col">
              {viewArrangement === 'horizontal' ? (
                // Side-by-side layout with draggable borders
                <div className="flex-1 flex flex-row overflow-hidden">
                  {documentsToDisplay.map((doc, arrayIdx, arr) => {
                      const panelWidth = docPanelWidths[arrayIdx] || (100 / arr.length);

                      return (
                        <React.Fragment key={doc.id}>
                          <div className="overflow-hidden flex flex-col" style={{ width: `${panelWidth}%` }}>
                            {doc.type === 'oa' && (
                              loadingOaPdf ? (
                                <div className="flex flex-col items-center justify-center h-full">
                                  <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                                  <p className="text-sm text-gray-600">{oaPdfStatus || 'Loading PDF...'}</p>
                                </div>
                              ) : oaPdfUrl ? (
                                <div className="flex flex-col h-full overflow-auto p-2">
                                  <Document
                                    file={oaPdfUrl}
                                    onLoadSuccess={({ numPages }) => {
                                      setOaNumPages(numPages);
                                      setLoadingOaPdf(false);
                                    }}
                                    onLoadError={(error) => {
                                      console.error('Error loading OA PDF:', error);
                                      setLoadingOaPdf(false);
                                    }}
                                  >
                                    {Array.from(new Array(oaNumPages), (_, index) => (
                                      <Page
                                        key={`oa-page-${index + 1}`}
                                        pageNumber={index + 1}
                                        width={Math.min(800, window.innerWidth * (panelWidth / 100) - 40)}
                                        className="mb-4 shadow-lg mx-auto"
                                      />
                                    ))}
                                  </Document>
                                </div>
                              ) : null
                            )}
                            {doc.type === 'pros' && (
                              loadingProsPdf ? (
                                <div className="flex flex-col items-center justify-center h-full">
                                  <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                                  <p className="text-sm text-gray-600">{prosPdfStatus || 'Loading PDF...'}</p>
                                </div>
                              ) : prosPdfUrl ? (
                                <div className="flex flex-col h-full overflow-auto p-2">
                                  <Document
                                    file={prosPdfUrl}
                                    onLoadSuccess={({ numPages }) => {
                                      setProsNumPages(numPages);
                                      setLoadingProsPdf(false);
                                    }}
                                    onLoadError={(error) => {
                                      console.error('Error loading Pros Doc PDF:', error);
                                      setLoadingProsPdf(false);
                                    }}
                                  >
                                    {Array.from(new Array(prosNumPages), (_, index) => (
                                      <Page
                                        key={`pros-page-${index + 1}`}
                                        pageNumber={index + 1}
                                        width={Math.min(800, window.innerWidth * (panelWidth / 100) - 40)}
                                        className="mb-4 shadow-lg mx-auto"
                                      />
                                    ))}
                                  </Document>
                                </div>
                              ) : null
                            )}
                            {doc.type === 'reference' && (
                              <DocumentViewer
                                document={doc.data.doc}
                                onClose={() => removeReferenceDoc(doc.id)}
                                isFullWidth={false}
                              />
                            )}
                            {doc.type === 'task' && (
                              <div className="h-full overflow-auto p-4 bg-white">
                                {doc.data.task_type === 'oa_extract' && (
                                  <OAExtractDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_analyze' && (
                                  <OAAnalyzeDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_summary_art' && (
                                  <OASummaryArtDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_timeline' && (
                                  <OATimelineDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_reporting_email' && (
                                  <OAEmailResultView data={doc.data.result} showOpenInEmail={false} />
                                )}
                              </div>
                            )}
                            {doc.type === 'draft' && (
                              <div className="h-full flex flex-col p-2 bg-white">
                                <div className="mb-2 flex items-center gap-1">
                                  <Input
                                    value={draftTitle}
                                    onChange={(e) => setDraftTitle(e.target.value)}
                                    className="flex-1 text-sm font-semibold"
                                    placeholder="Draft title..."
                                  />
                                  <Button
                                    onClick={handleExportDraft}
                                    variant="outline"
                                    size="sm"
                                    className="px-2"
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    onClick={handleSaveDraft}
                                    disabled={savingDraft}
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                  >
                                    {savingDraft ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                  </Button>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                  <LineNumberedTextarea
                                    value={draftContent}
                                    onChange={setDraftContent}
                                    placeholder="Start typing..."
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          {arrayIdx < arr.length - 1 && (
                            <div
                              className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-colors flex-shrink-0"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizingSplit(true);
                                setResizingSplitIndex(arrayIdx);
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
              ) : viewArrangement === 'vertical' ? (
                // Stacked layout with draggable borders
                <div className="flex-1 flex flex-col overflow-hidden">
                  {documentsToDisplay.map((doc, arrayIdx, arr) => {
                      const panelHeight = docPanelHeights[arrayIdx] || (100 / arr.length);

                      return (
                        <React.Fragment key={doc.id}>
                          <div className="overflow-hidden flex flex-col" style={{ height: `${panelHeight}%` }}>
                            {doc.type === 'oa' && (
                              loadingOaPdf ? (
                                <div className="flex flex-col items-center justify-center h-full">
                                  <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                                </div>
                              ) : oaPdfUrl ? (
                                <div className="flex flex-col h-full overflow-auto p-2">
                                  <Document
                                    file={oaPdfUrl}
                                    onLoadSuccess={({ numPages }) => {
                                      setOaNumPages(numPages);
                                      setLoadingOaPdf(false);
                                    }}
                                  >
                                    {Array.from(new Array(oaNumPages), (_, index) => (
                                      <Page
                                        key={`oa-page-${index + 1}`}
                                        pageNumber={index + 1}
                                        width={Math.min(800, window.innerWidth * 0.8)}
                                        className="mb-4 shadow-lg mx-auto"
                                      />
                                    ))}
                                  </Document>
                                </div>
                              ) : null
                            )}
                            {doc.type === 'pros' && (
                              loadingProsPdf ? (
                                <div className="flex flex-col items-center justify-center h-full">
                                  <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                                </div>
                              ) : prosPdfUrl ? (
                                <div className="flex flex-col h-full overflow-auto p-2">
                                  <Document
                                    file={prosPdfUrl}
                                    onLoadSuccess={({ numPages }) => {
                                      setProsNumPages(numPages);
                                      setLoadingProsPdf(false);
                                    }}
                                  >
                                    {Array.from(new Array(prosNumPages), (_, index) => (
                                      <Page
                                        key={`pros-page-${index + 1}`}
                                        pageNumber={index + 1}
                                        width={Math.min(800, window.innerWidth * 0.8)}
                                        className="mb-4 shadow-lg mx-auto"
                                      />
                                    ))}
                                  </Document>
                                </div>
                              ) : null
                            )}
                            {doc.type === 'reference' && (
                              <DocumentViewer
                                document={doc.data.doc}
                                onClose={() => removeReferenceDoc(doc.id)}
                                isFullWidth={false}
                              />
                            )}
                            {doc.type === 'task' && (
                              <div className="h-full overflow-auto p-4 bg-white">
                                {doc.data.task_type === 'oa_extract' && (
                                  <OAExtractDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_analyze' && (
                                  <OAAnalyzeDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_summary_art' && (
                                  <OASummaryArtDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_timeline' && (
                                  <OATimelineDisplay result={doc.data.result} />
                                )}
                                {doc.data.task_type === 'oa_reporting_email' && (
                                  <OAEmailResultView data={doc.data.result} showOpenInEmail={false} />
                                )}
                              </div>
                            )}
                            {doc.type === 'draft' && (
                              <div className="h-full flex flex-col p-2 bg-white">
                                <div className="mb-2 flex items-center gap-1">
                                  <Input
                                    value={draftTitle}
                                    onChange={(e) => setDraftTitle(e.target.value)}
                                    className="flex-1 text-sm font-semibold"
                                    placeholder="Draft title..."
                                  />
                                  <Button
                                    onClick={handleExportDraft}
                                    variant="outline"
                                    size="sm"
                                    className="px-2"
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    onClick={handleSaveDraft}
                                    disabled={savingDraft}
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                  >
                                    {savingDraft ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                  </Button>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                  <LineNumberedTextarea
                                    value={draftContent}
                                    onChange={setDraftContent}
                                    placeholder="Start typing..."
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          {arrayIdx < arr.length - 1 && (
                            <div
                              className="h-1 bg-gray-300 hover:bg-blue-500 cursor-row-resize transition-colors flex-shrink-0"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizingSplit(true);
                                setResizingSplitIndex(arrayIdx);
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
              ) : (
                // Grid layout (for 3-4 documents)
                (() => {
                  // Use the flat list of documents to display
                  const visibleDocs = documentsToDisplay;

                  // Render individual document
                  const renderDoc = (doc: typeof documentsToDisplay[0]) => {
                    if (doc.type === 'oa') {
                      return loadingOaPdf ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
                        </div>
                      ) : oaPdfUrl ? (
                        <div className="flex flex-col items-center p-2 h-full overflow-auto">
                          <Document file={oaPdfUrl} onLoadSuccess={({ numPages }) => { setOaNumPages(numPages); setLoadingOaPdf(false); }}>
                            {Array.from(new Array(oaNumPages), (_, index) => (
                              <Page
                                key={`oa-page-${index + 1}`}
                                pageNumber={index + 1}
                                width={Math.min(500, window.innerWidth * 0.4)}
                                className="mb-2 shadow mx-auto"
                              />
                            ))}
                          </Document>
                        </div>
                      ) : null;
                    }
                    if (doc.type === 'pros') {
                      return loadingProsPdf ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
                        </div>
                      ) : prosPdfUrl ? (
                        <div className="flex flex-col items-center p-2 h-full overflow-auto">
                          <Document file={prosPdfUrl} onLoadSuccess={({ numPages }) => { setProsNumPages(numPages); setLoadingProsPdf(false); }}>
                            {Array.from(new Array(prosNumPages), (_, index) => (
                              <Page
                                key={`pros-page-${index + 1}`}
                                pageNumber={index + 1}
                                width={Math.min(500, window.innerWidth * 0.4)}
                                className="mb-2 shadow mx-auto"
                              />
                            ))}
                          </Document>
                        </div>
                      ) : null;
                    }
                    if (doc.type === 'reference') {
                      return (
                        <DocumentViewer
                          document={doc.data.doc}
                          onClose={() => removeReferenceDoc(doc.id)}
                          isFullWidth={false}
                        />
                      );
                    }
                    if (doc.type === 'task') {
                      return (
                        <div className="h-full overflow-auto p-4">
                          {doc.data.task_type === 'oa_extract' && (
                            <OAExtractDisplay result={doc.data.result} />
                          )}
                          {doc.data.task_type === 'oa_analyze' && (
                            <OAAnalyzeDisplay result={doc.data.result} />
                          )}
                          {doc.data.task_type === 'oa_summary_art' && (
                            <OASummaryArtDisplay result={doc.data.result} />
                          )}
                          {doc.data.task_type === 'oa_timeline' && (
                            <OATimelineDisplay result={doc.data.result} />
                          )}
                        </div>
                      );
                    }
                    if (doc.type === 'draft') {
                      return (
                        <div className="h-full flex flex-col p-2 bg-white">
                          <div className="mb-2 flex items-center gap-1">
                            <Input
                              value={draftTitle}
                              onChange={(e) => setDraftTitle(e.target.value)}
                              className="flex-1 text-xs font-semibold"
                              placeholder="Draft title..."
                            />
                            <Button
                              onClick={handleExportDraft}
                              variant="outline"
                              size="sm"
                              className="px-2"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                            <Button
                              onClick={handleSaveDraft}
                              disabled={savingDraft}
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              {savingDraft ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <LineNumberedTextarea
                              value={draftContent}
                              onChange={setDraftContent}
                              placeholder="Start typing..."
                            />
                          </div>
                        </div>
                      );
                    }
                    return null;
                  };

                  if (numDocsShown === 3) {
                    // 3-doc grid: left column (2 docs stacked) + right column (1 doc full height)
                    return (
                      <div className="flex-1 flex flex-row overflow-hidden p-1">
                        {/* Left column with 2 stacked documents */}
                        <div className="flex flex-col overflow-hidden" style={{ width: `${gridColumnSplit}%` }}>
                          <div className="overflow-hidden bg-white border border-gray-200 rounded" style={{ height: `${gridLeftRowSplit}%` }}>
                            {renderDoc(visibleDocs[0])}
                          </div>

                          {/* Horizontal resize handle between left column docs */}
                          <div
                            className="h-1 bg-gray-300 hover:bg-blue-500 cursor-row-resize transition-colors flex-shrink-0 my-1"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsResizingGridLeftRow(true);
                            }}
                          />

                          <div className="flex-1 overflow-hidden bg-white border border-gray-200 rounded">
                            {renderDoc(visibleDocs[1])}
                          </div>
                        </div>

                        {/* Vertical resize handle */}
                        <div
                          className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-colors flex-shrink-0 mx-1"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setIsResizingGridColumn(true);
                          }}
                        />

                        {/* Right column with single document */}
                        <div className="flex-1 overflow-hidden bg-white border border-gray-200 rounded">
                          {renderDoc(visibleDocs[2])}
                        </div>
                      </div>
                    );
                  } else {
                    // 4-doc grid: 2x2 grid with resizable borders
                    return (
                      <div className="flex-1 flex flex-row overflow-hidden p-1">
                        {/* Left column with 2 stacked documents */}
                        <div className="flex flex-col overflow-hidden" style={{ width: `${gridColumnSplit}%` }}>
                          <div className="overflow-hidden bg-white border border-gray-200 rounded" style={{ height: `${gridLeftRowSplit}%` }}>
                            {renderDoc(visibleDocs[0])}
                          </div>

                          {/* Horizontal resize handle between left column docs */}
                          <div
                            className="h-1 bg-gray-300 hover:bg-blue-500 cursor-row-resize transition-colors flex-shrink-0 my-1"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsResizingGridLeftRow(true);
                            }}
                          />

                          <div className="flex-1 overflow-hidden bg-white border border-gray-200 rounded">
                            {renderDoc(visibleDocs[1])}
                          </div>
                        </div>

                        {/* Vertical resize handle between columns */}
                        <div
                          className="w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize transition-colors flex-shrink-0 mx-1"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setIsResizingGridColumn(true);
                          }}
                        />

                        {/* Right column with 2 stacked documents */}
                        <div className="flex flex-col overflow-hidden flex-1">
                          <div className="overflow-hidden bg-white border border-gray-200 rounded" style={{ height: `${gridRightRowSplit}%` }}>
                            {renderDoc(visibleDocs[2])}
                          </div>

                          {/* Horizontal resize handle between right column docs */}
                          <div
                            className="h-1 bg-gray-300 hover:bg-blue-500 cursor-row-resize transition-colors flex-shrink-0 my-1"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsResizingGridRightRow(true);
                            }}
                          />

                          <div className="flex-1 overflow-hidden bg-white border border-gray-200 rounded">
                            {renderDoc(visibleDocs[3])}
                          </div>
                        </div>
                      </div>
                    );
                  }
                })()
              )}
            </div>
          )}
        </div>
      </div>

      {/* Task Panel - Right Side Sliding Panel */}
      {showTaskPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowTaskPanel(false)}
          />

          {/* Sliding Panel */}
          <div className="fixed right-0 top-0 h-full w-[400px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Panel Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Gavel className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Document Analysis Tools</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTaskPanel(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="flex flex-col gap-3 px-3">
                <div className="text-sm text-gray-700 font-medium mb-2">Task History</div>
                <Button
                  disabled={!selectedApplicationNumber || loadingDocTaskHistory}
                  onClick={loadDocTaskHistory}
                  variant="secondary"
                  size="lg"
                  className="w-full"
                >
                  {loadingDocTaskHistory ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      View Document Task History
                    </>
                  )}
                </Button>
                <Button
                  disabled={!selectedApplicationNumber || loadingTaskHistory}
                  onClick={loadOATaskHistory}
                  variant="secondary"
                  size="lg"
                  className="w-full"
                >
                  {loadingTaskHistory ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      View Application Task History
                    </>
                  )}
                </Button>
                {/* Document Task History Display */}
                {showDocTaskHistory && (
                  <div className="mt-4">
                    <Separator className="mb-4" />
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Document Task History ({docTaskHistory.length})</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDocTaskHistory(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {docTaskHistory.length > 0 ? (
                        docTaskHistory.map((historyItem, index) => (
                          <div
                            key={historyItem.id || index}
                            className={`p-3 rounded-md border ${
                              historyItem.isCurrentDocument
                                ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-400'
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-sm text-gray-900">{historyItem.task_name}</h4>
                                  {historyItem.isCurrentDocument && (
                                    <Badge className="bg-blue-500 text-white text-xs">
                                      Current OA
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {historyItem.timestamp.toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full mt-2"
                              onClick={() => {
                                const parsedResult = getParsedResult(historyItem.result);
                                setTaskResult({
                                  task_type: historyItem.task_type,
                                  task_name: historyItem.task_name,
                                  result: parsedResult,
                                  timestamp: historyItem.timestamp
                                });
                                setShowTaskResult(true);
                                setShowTaskPanel(false);
                              }}
                            >
                              View Result
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">No task history found</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Application Task History Display */}
                {showTaskHistory && (
                  <div className="mt-4">
                    <Separator className="mb-4" />
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Application Task History ({oaTaskHistory.length})</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowTaskHistory(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {oaTaskHistory.length > 0 ? (
                        oaTaskHistory.map((historyItem, index) => (
                          <div
                            key={historyItem.id || index}
                            className="p-3 rounded-md border border-gray-200 bg-white"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-medium text-sm text-gray-900">{historyItem.task_name}</h4>
                                <div className="text-xs text-gray-500 mt-1">
                                  {historyItem.timestamp.toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full mt-2"
                              onClick={() => {
                                const parsedResult = getParsedResult(historyItem.result);
                                setTaskResult({
                                  task_type: historyItem.task_type,
                                  task_name: historyItem.task_name,
                                  result: parsedResult,
                                  timestamp: historyItem.timestamp
                                });
                                setShowTaskResult(true);
                                setShowTaskPanel(false);
                              }}
                            >
                              View Result
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">No task history found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Separator />
              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                <div className="space-y-4">
                  {!selectedOfficeActionDoc && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        Select an Office Action document from the dropdown to enable analysis tools.
                      </p>
                    </div>
                  )}

                  <div className="space-y-3">
                    {OA_TASKS.map((task) => (
                      <button
                        key={task.id}
                        disabled={!selectedOfficeActionDoc || taskProcessing}
                        onClick={() => setSelectedTask(task)}
                        className={`w-full text-left p-4 rounded-lg text-sm transition-all ${
                          !selectedOfficeActionDoc || taskProcessing
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                            : selectedTask?.id === task.id
                            ? 'bg-purple-50 border-2 border-purple-500 text-purple-900 shadow-md'
                            : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-gray-700'
                        }`}
                      >
                        <div className="font-semibold mb-2">{task.name}</div>
                        <div className="text-xs text-gray-600 mb-2">{task.description}</div>
                        {task.has_mpep && task.mpep_sections && (
                          <div className="inline-block text-xs px-2 py-1 rounded bg-gray-200 text-gray-700">
                            MPEP {task.mpep_sections.join(', ')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  <Separator />

                  {/* Email Template Selection for OA Reporting Email Task */}
                  {selectedTask?.id === 'oa_reporting_email' && (
                    <div className="space-y-2 mb-3">
                      <Label htmlFor="email-template-select-exam" className="text-sm font-medium">
                        Email Template
                      </Label>
                      <Select value={selectedEmailTemplate} onValueChange={setSelectedEmailTemplate}>
                        <SelectTrigger id="email-template-select-exam" className="truncate">
                          <SelectValue placeholder="Select an email template...">
                            {(() => {
                              // Find selected template and show only its name (truncated)
                              const customTemplate = userEmailTemplates.find((t: any) => t.prompt_id === selectedEmailTemplate);
                              if (customTemplate) {
                                return <span className="truncate">{customTemplate.name}</span>;
                              }
                              const defaultTemplate = OA_EMAIL_TEMPLATES.find(t => t.id === selectedEmailTemplate);
                              if (defaultTemplate) {
                                return <span className="truncate">{defaultTemplate.name}</span>;
                              }
                              return "Select an email template...";
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] overflow-y-auto">
                          {/* User Custom Templates - shown first */}
                          {userEmailTemplates.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                My Custom Templates
                              </div>
                              {userEmailTemplates.map((template: any) => (
                                <SelectItem key={template.prompt_id} value={template.prompt_id}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{template.name}</span>
                                    <span className="text-xs text-gray-500">
                                      {template.description || 'Custom template'}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}

                          {/* Default Templates */}
                          <div className={`px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 ${userEmailTemplates.length > 0 ? 'border-t' : ''}`}>
                            Default Templates
                          </div>
                          {OA_EMAIL_TEMPLATES.map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              <div className="flex flex-col">
                                <span className="font-medium">{template.name}</span>
                                <span className="text-xs text-gray-500">{template.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        Choose the email format and tone
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col gap-3">
                    <Button
                      disabled={!selectedOfficeActionDoc || !selectedTask || taskProcessing}
                      onClick={() => {
                        if (selectedTask) {
                          handleRunTask(selectedTask);
                          setShowTaskPanel(false);
                        }
                      }}
                      className="w-full"
                      size="lg"
                    >
                      {taskProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          {selectedTask ? `Run ${selectedTask.name}` : 'Select a Task First'}
                        </>
                      )}
                    </Button>

                    {selectedTask && (
                      <Button
                        onClick={() => setSelectedTask(null)}
                        variant="outline"
                        size="lg"
                        className="w-full"
                      >
                        Clear Selection
                      </Button>
                    )}

                    
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reference Documents Panel - Right Side Sliding Panel */}
      {showReferencePanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowReferencePanel(false)}
          />

          {/* Sliding Panel */}
          <div className="fixed right-0 top-0 h-full w-[500px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Panel Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Reference Documents</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReferencePanel(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                  {/* Document Selection Status and Arrangement Options */}
                  {(selectedReferenceDocs.length > 0 || showOfficeAction || showProsDoc || showTaskResultViewer) && (
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Document Viewing</h4>

                      {/* Show which documents are currently selected */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <Badge variant={showOfficeAction ? 'default' : 'outline'} className="text-xs">
                            Office Action {showOfficeAction ? '✓' : ''}
                          </Badge>
                          <Badge variant={showProsDoc ? 'default' : 'outline'} className="text-xs">
                            Pros Doc {showProsDoc ? '✓' : ''}
                          </Badge>
                          <Badge variant={showReference ? 'default' : 'outline'} className="text-xs">
                            References ({selectedReferenceDocs.length}) {showReference ? '✓' : ''}
                          </Badge>
                          <Badge variant={showTaskResultViewer ? 'default' : 'outline'} className="text-xs">
                            Task Result {showTaskResultViewer ? '✓' : ''}
                          </Badge>
                        </div>

                        {/* Button to open arrangement modal */}
                        {numDocsShown >= 2 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => setShowArrangementModal(true)}
                          >
                            <List className="w-4 h-4 mr-2" />
                            Change Layout ({viewArrangement === 'horizontal' ? 'Side-by-Side' : viewArrangement === 'vertical' ? 'Stacked' : 'Grid'})
                          </Button>
                        )}
                      </div>

                      {selectedReferenceDocs.length > 0 && (
                        <>
                          <Separator className="my-4" />

                          <Button
                            variant="destructive"
                            size="sm"
                            className="w-full"
                            onClick={() => removeAllReferenceDocs()}
                          >
                            Close All Reference Documents ({selectedReferenceDocs.length})
                          </Button>
                        </>
                      )}
                    </div>
                  )}

                  <Tabs defaultValue="my-docs" className="w-full">
                    <TabsList className="grid w-full grid-cols-9">
                      <TabsTrigger value="my-docs">Apps</TabsTrigger>
                      <TabsTrigger value="patent">Patent</TabsTrigger>
                      <TabsTrigger value="publication">Pub</TabsTrigger>
                      <TabsTrigger value="foreign_patent">Foreign</TabsTrigger>
                      <TabsTrigger value="upload">Upload</TabsTrigger>
                      <TabsTrigger value="drafts">Drafts</TabsTrigger>
                      <TabsTrigger value="mpep">MPEP</TabsTrigger>
                      <TabsTrigger value="cfr">CFR</TabsTrigger>
                      <TabsTrigger value="usc">USC</TabsTrigger>
                    </TabsList>

                    <TabsContent value="my-docs" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div className="text-xs font-medium text-gray-500 mb-2">ACTIVE DOCUMENTS</div>

                        {/* Currently Selected Application */}
                        {selectedAppData && (
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-gray-700 uppercase">Current Application</div>
                            <div className="space-y-1">
                              <div className="w-full border border-gray-200 rounded p-2 bg-gray-50">
                                <div className="flex flex-col items-start w-full">
                                  <div className="font-medium text-sm">
                                    {selectedAppData.applicationNumber || selectedApplicationNumber}
                                  </div>
                                  <div className="text-xs text-gray-600 line-clamp-2">
                                    {selectedAppData.inventionTitle}
                                  </div>
                                </div>
                              </div>

                              {/* Show associated patent/publication from metadata */}
                              {(selectedAppData?.patentNumber || selectedAppData?.earliestPublicationNumber) && (
                                <div className="ml-4 space-y-1">
                                  {selectedAppData.patentNumber && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-1 text-xs"
                                      onClick={() => {
                                        addReferenceDoc({
                                          file_name: selectedAppData.patentNumber,
                                          resource_id: selectedAppData.patentNumber,
                                          file_type: 'patent',
                                          metadata: selectedAppData
                                        }, 'patent');
                                      }}
                                    >
                                      <div className="flex items-center gap-2 w-full">
                                        <FileText className="w-3 h-3 text-blue-500" />
                                        <span className="text-xs text-gray-700">Patent: {selectedAppData.patentNumber}</span>
                                      </div>
                                    </Button>
                                  )}
                                  {selectedAppData.earliestPublicationNumber && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-1 text-xs"
                                      onClick={() => {
                                        addReferenceDoc({
                                          file_name: selectedAppData.earliestPublicationNumber,
                                          resource_id: selectedAppData.earliestPublicationNumber,
                                          file_type: 'publication',
                                          metadata: selectedAppData
                                        }, 'publication');
                                      }}
                                    >
                                      <div className="flex items-center gap-2 w-full">
                                        <FileText className="w-3 h-3 text-green-500" />
                                        <span className="text-xs text-gray-700">Pub: {selectedAppData.earliestPublicationNumber}</span>
                                      </div>
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="text-xs font-medium text-gray-500 mb-2">SESSION DOCUMENTS</div>
                        <ScrollArea className="h-[400px]">
                          <div className="space-y-3 pr-4">
                            {/* Removed Office Actions, Claims, and Other Prosecution Documents - they're available in the main pros doc viewer */}
                            {false && oaDocs.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-3 h-3 text-blue-600" />
                                  <p className="text-xs font-semibold text-gray-700 uppercase">Office Actions ({oaDocs.length})</p>
                                </div>
                                {oaDocs.map((doc) => {
                                  const isActive = selectedOfficeActionDoc?.docId === doc.docId;
                                  if (isActive) return null; // Skip active (shown above)

                                  return (
                                    <Button
                                      key={doc.docId}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-2"
                                      onClick={() => {
                                        addReferenceDoc(doc, 'patent');
                                        // Don't close panel - let user select viewing arrangement
                                      }}
                                    >
                                      <div className="flex flex-col items-start w-full">
                                        <div className="text-sm font-medium line-clamp-2">
                                          {doc.description}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {doc.date}
                                        </div>
                                      </div>
                                    </Button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Claims Documents */}
                            {false && claimsDocs.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <FileStack className="w-3 h-3 text-green-600" />
                                  <p className="text-xs font-semibold text-gray-700 uppercase">Claims ({claimsDocs.length})</p>
                                </div>
                                {claimsDocs.map((doc) => {
                                  const isActive = selectedProsDoc?.docId === doc.docId;
                                  if (isActive) return null; // Skip active (shown above)

                                  return (
                                    <Button
                                      key={doc.docId}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-2"
                                      onClick={() => {
                                        addReferenceDoc(doc, 'patent');
                                        // Don't close panel - let user select viewing arrangement
                                      }}
                                    >
                                      <div className="flex flex-col items-start w-full">
                                        <div className="text-sm font-medium line-clamp-2">
                                          {doc.description}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {doc.date}
                                        </div>
                                      </div>
                                    </Button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Other Prosecution Documents */}
                            {false && specDocs.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <File className="w-3 h-3 text-gray-600" />
                                  <p className="text-xs font-semibold text-gray-700 uppercase">Other Documents ({specDocs.length})</p>
                                </div>
                                {specDocs.map((doc) => {
                                  const isActive = selectedProsDoc?.docId === doc.docId;
                                  if (isActive) return null; // Skip active (shown above)

                                  return (
                                    <Button
                                      key={doc.docId}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-2"
                                      onClick={() => {
                                        addReferenceDoc(doc, 'patent');
                                        // Don't close panel - let user select viewing arrangement
                                      }}
                                    >
                                      <div className="flex flex-col items-start w-full">
                                        <div className="text-sm font-medium line-clamp-2">
                                          {doc.description}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {doc.date}
                                        </div>
                                      </div>
                                    </Button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Session Applications with their Documents */}
                            {sessionApplications && sessionApplications.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Database className="w-3 h-3 text-purple-600" />
                                  <p className="text-xs font-semibold text-gray-700 uppercase">Session Applications ({sessionApplications.filter((app: any) => {
                                    const appData = app.applicationMetaData || app.application?.applicationMetaData;
                                    return appData?.inventionTitle;
                                  }).length})</p>
                                </div>
                                {sessionApplications.map((app: any, index: number) => {
                                  const appData = app.applicationMetaData || app.application?.applicationMetaData;
                                  const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
                                  const isActive = appId === selectedApplicationNumber;

                                  // Skip if no title
                                  if (!appData?.inventionTitle) return null;

                                  if (isActive) return null; // Skip active (shown above)

                                  // Get associated patent and publication from the app metadata
                                  const hasPatent = appData?.patentNumber;
                                  const hasPublication = appData?.earliestPublicationNumber;

                                  return (
                                    <div key={appId || index} className="space-y-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        onClick={() => {
                                          onDocumentSelect({
                                            type: 'application',
                                            file_type: 'application',
                                            resource_id: `application-${appId}`,
                                            title: appId,
                                            application_id: appId,
                                            application: app.application || app,
                                            applicationMetaData: appData
                                          });
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="font-medium text-sm">
                                            {appId}
                                          </div>
                                          <div className="text-xs text-gray-600 line-clamp-2 mt-1">
                                            {appData?.inventionTitle || 'No title available'}
                                          </div>
                                          {appData?.filingDate && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              Filed: {appData.filingDate}
                                            </div>
                                          )}
                                        </div>
                                      </Button>

                                      {/* Show associated patent/publication from metadata */}
                                      {(hasPatent || hasPublication) && (
                                        <div className="ml-4 space-y-1">
                                          {hasPatent && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="w-full justify-start text-left h-auto py-1 text-xs"
                                              onClick={() => {
                                                addReferenceDoc({
                                                  file_name: appData.patentNumber,
                                                  resource_id: appData.patentNumber,
                                                  file_type: 'patent',
                                                  metadata: appData
                                                }, 'patent');
                                                // Don't close panel - let user select viewing arrangement
                                              }}
                                            >
                                              <div className="flex items-center gap-2 w-full">
                                                <FileText className="w-3 h-3 text-blue-500" />
                                                <span className="text-xs text-gray-700">Patent: {appData.patentNumber}</span>
                                              </div>
                                            </Button>
                                          )}
                                          {hasPublication && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="w-full justify-start text-left h-auto py-1 text-xs"
                                              onClick={() => {
                                                addReferenceDoc({
                                                  file_name: appData.earliestPublicationNumber,
                                                  resource_id: appData.earliestPublicationNumber,
                                                  file_type: 'publication',
                                                  metadata: appData
                                                }, 'publication');
                                                // Don't close panel - let user select viewing arrangement
                                              }}
                                            >
                                              <div className="flex items-center gap-2 w-full">
                                                <FileText className="w-3 h-3 text-green-500" />
                                                <span className="text-xs text-gray-700">Pub: {appData.earliestPublicationNumber}</span>
                                              </div>
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Empty State */}
                            {oaDocs.length === 0 && claimsDocs.length === 0 && specDocs.length === 0 &&
                             (!sessionApplications || sessionApplications.length === 0) && (
                              <div className="text-sm text-gray-500 text-center py-8">
                                <File className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                <p>No documents available</p>
                                <p className="text-xs mt-1">Documents will appear here once loaded</p>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </TabsContent>

                    <TabsContent value="patent" className="space-y-4 mt-4">
                      {(() => {
                        const patentDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'patent'
                        ) || [];

                        // Check if current reference is a patent
                        const activeDoc = selectedReferenceDocs.find(ref => ref.type === 'patent')?.doc || null;

                        // Get active patents from session context
                        const activePatents = sessionContext?.current_patents || [];

                        // Use loaded patents if available, otherwise use session patents
                        const basePatentDocs = patientsLoaded ? allPatentDocs : patentDocs;

                        // Add active patents that aren't in the list yet
                        const combinedPatentDocs = [...basePatentDocs];
                        activePatents.forEach((patentId: string) => {
                          if (!patentId) return;
                          // Check if this patent is already in the list
                          const cleanPatentId = patentId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                          const exists = basePatentDocs.some((doc: any) => {
                            const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanDoc === cleanPatentId || doc.resource_id === patentId;
                          });
                          if (!exists) {
                            // Add as a minimal document object
                            combinedPatentDocs.push({
                              resource_id: patentId,
                              file_name: patentId,
                              file_type: 'patent',
                              fromActiveContext: true
                            });
                          }
                        });

                        // Sort with active patents from session context first, then currently selected reference doc
                        const sortedDocs = [...combinedPatentDocs].sort((a, b) => {
                          const aIsInActiveContext = activePatents.some((p: string) => {
                            // Clean the patent number for comparison
                            const cleanA = a.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanA === cleanP || a.resource_id === p;
                          });
                          const bIsInActiveContext = activePatents.some((p: string) => {
                            const cleanB = b.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanB === cleanP || b.resource_id === p;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;

                          // If both are or aren't in active context, sort by currently selected reference
                          if (activeDoc && a.resource_id === activeDoc.resource_id) return -1;
                          if (activeDoc && b.resource_id === activeDoc.resource_id) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!patientsLoaded && (
                              <Button
                                onClick={loadAllPatents}
                                disabled={loadingPatents}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingPatents ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Patents...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Load All Patents
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isCurrentlySelected = activeDoc && doc.resource_id === activeDoc.resource_id;
                                    const isInActiveContext = activePatents.some((p: string) => {
                                      const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                      const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                      return cleanDoc === cleanP || doc.resource_id === p;
                                    });
                                    return (
                                      <Button
                                        key={doc.resource_id}
                                        variant={isCurrentlySelected ? 'default' : 'outline'}
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        onClick={() => {
                                          addReferenceDoc(doc, 'patent');
                                          // Don't close panel - let user select viewing arrangement
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="flex items-center gap-2 w-full">
                                            <FileText className="w-4 h-4 flex-shrink-0" />
                                            <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                            {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                          </div>
                                          {doc.upload_date && (
                                            <div className="text-xs text-gray-500 mt-1 ml-6">
                                              Added: {new Date(doc.upload_date).toLocaleDateString()}
                                            </div>
                                          )}
                                        </div>
                                      </Button>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-8">
                                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">No patents in session</p>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="publication" className="space-y-4 mt-4">
                      {(() => {
                        const publicationDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'publication'
                        ) || [];

                        // Check if current reference is a publication
                        const activeDoc = selectedReferenceDocs.find(ref => ref.type === 'publication')?.doc || null;

                        // Get active publications from session context
                        const activePublications = sessionContext?.current_publications || [];

                        // Use loaded publications if available, otherwise use session publications
                        const basePublicationDocs = publicationsLoaded ? allPublicationDocs : publicationDocs;

                        // Add active publications that aren't in the list yet
                        const combinedPublicationDocs = [...basePublicationDocs];
                        activePublications.forEach((publicationId: string) => {
                          if (!publicationId) return;
                          // Check if this publication is already in the list
                          const cleanPublicationId = publicationId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                          const exists = basePublicationDocs.some((doc: any) => {
                            const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanDoc === cleanPublicationId || doc.resource_id === publicationId;
                          });
                          if (!exists) {
                            // Add as a minimal document object
                            combinedPublicationDocs.push({
                              resource_id: publicationId,
                              file_name: publicationId,
                              file_type: 'publication',
                              fromActiveContext: true
                            });
                          }
                        });

                        // Sort with active publications from session context first, then currently selected reference doc
                        const sortedDocs = [...combinedPublicationDocs].sort((a, b) => {
                          const aIsInActiveContext = activePublications.some((p: string) => {
                            const cleanA = a.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanA === cleanP || a.resource_id === p;
                          });
                          const bIsInActiveContext = activePublications.some((p: string) => {
                            const cleanB = b.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanB === cleanP || b.resource_id === p;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;

                          // If both are or aren't in active context, sort by currently selected reference
                          if (activeDoc && a.resource_id === activeDoc.resource_id) return -1;
                          if (activeDoc && b.resource_id === activeDoc.resource_id) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!publicationsLoaded && (
                              <Button
                                onClick={loadAllPublications}
                                disabled={loadingPublications}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingPublications ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Publications...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Load All Publications
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isCurrentlySelected = activeDoc && doc.resource_id === activeDoc.resource_id;
                                    const isInActiveContext = activePublications.some((p: string) => {
                                    const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                    const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                    return cleanDoc === cleanP || doc.resource_id === p;
                                  });
                                  return (
                                    <Button
                                      key={doc.resource_id}
                                      variant={isCurrentlySelected ? 'default' : 'outline'}
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-2"
                                      onClick={() => {
                                        addReferenceDoc(doc, 'publication');
                                        // Don't close panel - let user select viewing arrangement
                                      }}
                                    >
                                      <div className="flex flex-col items-start w-full">
                                        <div className="flex items-center gap-2 w-full">
                                          <FileText className="w-4 h-4 flex-shrink-0" />
                                          <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                          {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                        </div>
                                        {doc.upload_date && (
                                          <div className="text-xs text-gray-500 mt-1 ml-6">
                                            Added: {new Date(doc.upload_date).toLocaleDateString()}
                                          </div>
                                        )}
                                      </div>
                                    </Button>
                                  );
                                })
                              ) : (
                                <div className="text-center py-8">
                                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-600">No publications in session</p>
                                </div>
                              )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="upload" className="space-y-4 mt-4">
                      {(() => {
                        const uploadDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'upload'
                        ) || [];

                        // Check if current reference is an upload
                        const activeDoc = selectedReferenceDocs.find(ref => ref.type === 'upload')?.doc || null;

                        // Get active uploads from session context
                        const activeUploads = sessionContext?.resource_names || [];

                        // Use loaded uploads if available, otherwise use session uploads
                        const baseUploadDocs = uploadsLoaded ? allUploadDocs : uploadDocs;

                        // Add active uploads that aren't in the list yet
                        const combinedUploadDocs = [...baseUploadDocs];
                        activeUploads.forEach((uploadName: string) => {
                          if (!uploadName) return;
                          // Check if this upload is already in the list
                          const exists = baseUploadDocs.some((doc: any) => {
                            return doc.file_name === uploadName || doc.resource_id === uploadName;
                          });
                          if (!exists) {
                            // Add as a minimal document object
                            combinedUploadDocs.push({
                              resource_id: uploadName,
                              file_name: uploadName,
                              file_type: 'upload',
                              fromActiveContext: true
                            });
                          }
                        });

                        // Sort with active uploads from session context first, then currently selected reference doc
                        const sortedDocs = [...combinedUploadDocs].sort((a, b) => {
                          const aIsInActiveContext = activeUploads.some((u: string) => {
                            return a.file_name === u || a.resource_id === u;
                          });
                          const bIsInActiveContext = activeUploads.some((u: string) => {
                            return b.file_name === u || b.resource_id === u;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;

                          // If both are or aren't in active context, sort by currently selected reference
                          if (activeDoc && a.resource_id === activeDoc.resource_id) return -1;
                          if (activeDoc && b.resource_id === activeDoc.resource_id) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!uploadsLoaded && (
                              <Button
                                onClick={loadAllUploads}
                                disabled={loadingUploads}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingUploads ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Uploads...
                                  </>
                                ) : (
                                  <>
                                    <File className="w-4 h-4 mr-2" />
                                    Load All Uploads
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isCurrentlySelected = activeDoc && doc.resource_id === activeDoc.resource_id;
                                    const isInActiveContext = activeUploads.some((u: string) => {
                                      return doc.file_name === u || doc.resource_id === u;
                                    });
                                  return (
                                    <Button
                                      key={doc.resource_id}
                                      variant={isCurrentlySelected ? 'default' : 'outline'}
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-2"
                                      onClick={() => {
                                        addReferenceDoc(doc, 'upload');
                                        // Don't close panel - let user select viewing arrangement
                                      }}
                                    >
                                      <div className="flex flex-col items-start w-full">
                                        <div className="flex items-center gap-2 w-full">
                                          <File className="w-4 h-4 flex-shrink-0" />
                                          <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                          {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                        </div>
                                        {doc.upload_date && (
                                          <div className="text-xs text-gray-500 mt-1 ml-6">
                                            Uploaded: {new Date(doc.upload_date).toLocaleDateString()}
                                          </div>
                                        )}
                                        {doc.file_size && (
                                          <div className="text-xs text-gray-500 ml-6">
                                            {(doc.file_size / 1024).toFixed(1)} KB
                                          </div>
                                        )}
                                      </div>
                                    </Button>
                                  );
                                })
                              ) : (
                                <div className="text-center py-8">
                                  <File className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-600">No uploaded documents in session</p>
                                </div>
                              )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="foreign_patent" className="space-y-4 mt-4">
                      {(() => {
                        const foreignPatentDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'foreign_patent'
                        ) || [];

                        // Check if current reference is a foreign patent
                        const activeDoc = selectedReferenceDocs.find(ref => ref.type === 'foreign_patent')?.doc || null;

                        // Get active foreign patents from session context
                        const activeForeignPatents = sessionContext?.current_foreign_patents || [];

                        // Use loaded foreign patents if available, otherwise use session foreign patents
                        const baseForeignPatentDocs = foreignPatientsLoaded ? allForeignPatentDocs : foreignPatentDocs;

                        // Add active foreign patents that aren't in the list yet
                        const combinedForeignPatentDocs = [...baseForeignPatentDocs];
                        activeForeignPatents.forEach((foreignPatentId: string) => {
                          if (!foreignPatentId) return;
                          // Check if this foreign patent is already in the list
                          const exists = baseForeignPatentDocs.some((doc: any) => {
                            return doc.file_name === foreignPatentId || doc.resource_id === foreignPatentId;
                          });
                          if (!exists) {
                            // Add as a minimal document object
                            combinedForeignPatentDocs.push({
                              resource_id: foreignPatentId,
                              file_name: foreignPatentId,
                              file_type: 'foreign_patent',
                              fromActiveContext: true
                            });
                          }
                        });

                        // Sort with active foreign patents from session context first, then currently selected reference doc
                        const sortedDocs = [...combinedForeignPatentDocs].sort((a, b) => {
                          const aIsInActiveContext = activeForeignPatents.some((p: string) => {
                            return a.file_name === p || a.resource_id === p;
                          });
                          const bIsInActiveContext = activeForeignPatents.some((p: string) => {
                            return b.file_name === p || b.resource_id === p;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;

                          // If both are or aren't in active context, sort by currently selected reference
                          if (activeDoc && a.resource_id === activeDoc.resource_id) return -1;
                          if (activeDoc && b.resource_id === activeDoc.resource_id) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!foreignPatientsLoaded && (
                              <Button
                                onClick={loadAllForeignPatents}
                                disabled={loadingForeignPatents}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingForeignPatents ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Foreign Patents...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Load All Foreign Patents
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isCurrentlySelected = activeDoc && doc.resource_id === activeDoc.resource_id;
                                    const isInActiveContext = activeForeignPatents.some((p: string) => {
                                      return doc.file_name === p || doc.resource_id === p;
                                    });
                                  return (
                                    <Button
                                      key={doc.resource_id}
                                      variant={isCurrentlySelected ? 'default' : 'outline'}
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-2"
                                      onClick={() => {
                                        addReferenceDoc(doc, 'foreign_patent');
                                        // Don't close panel - let user select viewing arrangement
                                      }}
                                    >
                                      <div className="flex flex-col items-start w-full">
                                        <div className="flex items-center gap-2 w-full">
                                          <FileText className="w-4 h-4 flex-shrink-0" />
                                          <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                          {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                        </div>
                                        {doc.upload_date && (
                                          <div className="text-xs text-gray-500 mt-1 ml-6">
                                            Added: {new Date(doc.upload_date).toLocaleDateString()}
                                          </div>
                                        )}
                                      </div>
                                    </Button>
                                  );
                                })
                              ) : (
                                <div className="text-center py-8">
                                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-600">No foreign patents in session</p>
                                </div>
                              )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="drafts" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        {!draftsLoaded && (
                          <Button
                            onClick={loadAllDrafts}
                            disabled={loadingDrafts}
                            variant="outline"
                            className="w-full"
                          >
                            {loadingDrafts ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Loading All Drafts...
                              </>
                            ) : (
                              <>
                                <FileEdit className="w-4 h-4 mr-2" />
                                Load All Drafts
                              </>
                            )}
                          </Button>
                        )}
                        <ScrollArea className="h-[500px]">
                          <div className="space-y-2 pr-4">
                            {allDrafts.length > 0 ? (
                              allDrafts.map((draft: any) => (
                                <Button
                                  key={draft.draft_id}
                                  variant="outline"
                                  size="sm"
                                  className="w-full justify-start text-left h-auto py-2"
                                  onClick={() => handleOpenDraftInExamination(draft)}
                                >
                                  <div className="flex flex-col items-start w-full">
                                    <div className="flex items-center gap-2 w-full">
                                      <FileEdit className="w-4 h-4 flex-shrink-0 text-purple-600" />
                                      <div className="font-medium text-sm truncate">{draft.title || 'Untitled'}</div>
                                    </div>
                                    {draft.content && (
                                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                                        {draft.content.substring(0, 100)}{draft.content.length > 100 ? '...' : ''}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                        {draft.draft_type || 'general'}
                                      </Badge>
                                      {draft.updated_at && (
                                        <span className="text-xs text-gray-400">
                                          {new Date(draft.updated_at).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </Button>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <FileEdit className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">No drafts found</p>
                                {draftsLoaded && (
                                  <p className="text-xs mt-1">Create a draft in the chat area to see it here</p>
                                )}
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </TabsContent>

                    <TabsContent value="mpep" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        {/* Direct section input */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Load Specific Section</label>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="Enter MPEP section (e.g., 2141)"
                              className="flex-1"
                              value={mpepSectionInput}
                              onChange={(e) => setMpepSectionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleLoadMpepSection(mpepSectionInput);
                                }
                              }}
                            />
                            <Button
                              onClick={() => handleLoadMpepSection(mpepSectionInput)}
                              variant="default"
                              disabled={loadingMpepSection || !mpepSectionInput.trim()}
                            >
                              {loadingMpepSection ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Load'
                              )}
                            </Button>
                          </div>
                        </div>

                        <Separator />

                        {/* Browse MPEP structure */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Browse MPEP</label>
                          {loadingMpepStructure ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            </div>
                          ) : mpepStructure && Object.keys(mpepStructure).length > 0 ? (
                            <ScrollArea className="h-[400px]">
                              <div className="space-y-1 pr-4">
                                {(() => {
                                  // Parse MPEP structure - chapter keys are like "0100", "0200", etc.
                                  const chapters = Object.keys(mpepStructure)
                                    .filter(key => /^\d{4}$/.test(key) && key.endsWith('00'))
                                    .sort((a, b) => parseInt(a) - parseInt(b))
                                    .map(chapterKey => {
                                      const chapterData = mpepStructure[chapterKey];
                                      const chapterNum = parseInt(chapterKey).toString();

                                      // Extract section numbers from the sections object
                                      const sectionNumbers = chapterData.sections
                                        ? Object.keys(chapterData.sections).sort((a, b) => parseInt(a) - parseInt(b))
                                        : [];

                                      return {
                                        id: chapterNum,
                                        title: chapterData.title || `Chapter ${chapterNum}`,
                                        sections: sectionNumbers
                                      };
                                    });

                                  return chapters.map((chapter) => {
                                    const hasSections = chapter.sections && chapter.sections.length > 0;
                                    return (
                                      <Collapsible
                                        key={chapter.id}
                                        open={expandedMpepChapters.has(chapter.id)}
                                        onOpenChange={() => toggleMpepChapter(chapter.id)}
                                      >
                                        <CollapsibleTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start text-left h-auto py-2"
                                          >
                                            <ChevronDown
                                              className={`w-4 h-4 mr-2 transition-transform ${
                                                expandedMpepChapters.has(chapter.id) ? '' : '-rotate-90'
                                              }`}
                                            />
                                            <BookOpen className="w-4 h-4 mr-2" />
                                            <div className="flex-1">
                                              <div className="font-medium text-sm">Chapter {chapter.id}</div>
                                              <div className="text-xs text-muted-foreground">{chapter.title}</div>
                                              {hasSections && (
                                                <div className="text-xs text-muted-foreground mt-1">
                                                  {chapter.sections.length} sections
                                                </div>
                                              )}
                                            </div>
                                          </Button>
                                        </CollapsibleTrigger>
                                        {hasSections && (
                                          <CollapsibleContent>
                                            <div className="ml-6 mt-1 space-y-1">
                                              {chapter.sections.map((sectionNum: string) => (
                                                <Button
                                                  key={sectionNum}
                                                  variant="ghost"
                                                  size="sm"
                                                  className="w-full justify-start text-left h-auto py-1.5"
                                                  onClick={() => {
                                                    setMpepSectionInput(sectionNum);
                                                    handleLoadMpepSection(sectionNum);
                                                  }}
                                                >
                                                  <FileText className="w-3 h-3 mr-2 flex-shrink-0" />
                                                  <div className="flex-1 text-xs">
                                                    MPEP § {sectionNum}
                                                  </div>
                                                </Button>
                                              ))}
                                            </div>
                                          </CollapsibleContent>
                                        )}
                                      </Collapsible>
                                    );
                                  });
                                })()}
                              </div>
                            </ScrollArea>
                          ) : (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                              No MPEP structure available
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="cfr" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        {/* Direct section input */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Load Specific Section</label>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="Enter CFR citation (e.g., 1.111)"
                              className="flex-1"
                              value={cfrSectionInput}
                              onChange={(e) => setCfrSectionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleLoadCfrSection(cfrSectionInput);
                                }
                              }}
                            />
                            <Button
                              onClick={() => handleLoadCfrSection(cfrSectionInput)}
                              variant="default"
                              disabled={loadingCfrSection || !cfrSectionInput.trim()}
                            >
                              {loadingCfrSection ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Load'
                              )}
                            </Button>
                          </div>
                        </div>

                        <Separator />

                        {/* Browse CFR structure */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Browse 37 CFR</label>
                          {loadingCfrStructure ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            </div>
                          ) : cfrStructure && Array.isArray(cfrStructure) ? (
                            <ScrollArea className="h-[400px]">
                              <div className="space-y-1 pr-4 text-sm">
                                {(() => {
                                  // Find USPTO Chapter I
                                  const usptoChapter = cfrStructure.find((chapter: any) => chapter.number === 'I');
                                  if (!usptoChapter) {
                                    return <div className="text-center py-8 text-sm text-muted-foreground">USPTO chapter not found</div>;
                                  }

                                  return usptoChapter.children?.map((subchapter: any, subIdx: number) => (
                                    <div key={subIdx}>
                                      <div className="text-xs font-semibold text-gray-700 mb-2 mt-3">
                                        {subchapter.name}
                                      </div>

                                      {/* Iterate through parts */}
                                      {subchapter.children
                                        ?.slice()
                                        .sort((a: any, b: any) => {
                                          const numA = parseInt(a.number.split('-')[0]) || 0;
                                          const numB = parseInt(b.number.split('-')[0]) || 0;
                                          return numA - numB;
                                        })
                                        .map((part: any, partIdx: number) => {
                                          const partKey = `${subIdx}-${part.number}`;
                                          const hasSubparts = part.children?.some((c: any) => c.type === 'subpart');
                                          const directSections = part.children?.filter((c: any) => c.type === 'section') || [];

                                          return (
                                            <div key={partIdx} className="mb-1">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="w-full justify-start text-left h-auto py-2"
                                                onClick={() => toggleCfrPart(partKey)}
                                              >
                                                <ChevronDown
                                                  className={`w-4 h-4 mr-2 transition-transform ${
                                                    expandedCfrParts[partKey] ? '' : '-rotate-90'
                                                  }`}
                                                />
                                                <div className="flex-1">
                                                  <div className="text-xs text-muted-foreground mb-0.5">Part {part.number}</div>
                                                  <div className="text-xs leading-tight">{part.name.replace(/^PART \d+—?/, '')}</div>
                                                </div>
                                              </Button>

                                              {expandedCfrParts[partKey] && (
                                                <div className="ml-6 mt-1 space-y-0.5">
                                                  {/* If part has direct sections (no subparts) */}
                                                  {!hasSubparts && directSections.length > 0 && directSections.map((section: any, secIdx: number) => {
                                                    const cleanNumber = section.number.replace(/§\s*/, '').trim();
                                                    return (
                                                      <Button
                                                        key={secIdx}
                                                        variant="ghost"
                                                        size="sm"
                                                        className="w-full justify-start text-left h-auto py-1.5"
                                                        onClick={() => {
                                                          setCfrSectionInput(cleanNumber);
                                                          handleLoadCfrSection(cleanNumber);
                                                        }}
                                                      >
                                                        <FileText className="w-3 h-3 mr-2 flex-shrink-0" />
                                                        <div className="flex-1 text-xs">{section.name}</div>
                                                      </Button>
                                                    );
                                                  })}

                                                  {/* If part has subparts */}
                                                  {hasSubparts && part.children
                                                    ?.filter((c: any) => c.type === 'subpart')
                                                    .map((subpart: any, subpartIdx: number) => {
                                                      const subpartKey = `${partKey}-${subpartIdx}`;
                                                      const sections = subpart.children?.filter((c: any) => c.type === 'section') || [];

                                                      if (sections.length === 0) return null;

                                                      return (
                                                        <div key={subpartIdx}>
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="w-full justify-start text-left h-auto py-1.5 text-xs"
                                                            onClick={() => toggleCfrSubpart(subpartKey)}
                                                          >
                                                            <ChevronDown
                                                              className={`w-3 h-3 mr-2 transition-transform ${
                                                                expandedCfrSubparts[subpartKey] ? '' : '-rotate-90'
                                                              }`}
                                                            />
                                                            {subpart.name}
                                                          </Button>

                                                          {expandedCfrSubparts[subpartKey] && (
                                                            <div className="ml-5 space-y-0.5">
                                                              {sections.map((section: any, secIdx: number) => {
                                                                const cleanNumber = section.number.replace(/§\s*/, '').trim();
                                                                return (
                                                                  <Button
                                                                    key={secIdx}
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="w-full justify-start text-left h-auto py-1.5"
                                                                    onClick={() => {
                                                                      setCfrSectionInput(cleanNumber);
                                                                      handleLoadCfrSection(cleanNumber);
                                                                    }}
                                                                  >
                                                                    <FileText className="w-3 h-3 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 text-xs">{section.name}</div>
                                                                  </Button>
                                                                );
                                                              })}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                    </div>
                                  ));
                                })()}
                              </div>
                            </ScrollArea>
                          ) : (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                              No CFR structure available
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="usc" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <Input
                          type="text"
                          placeholder="Enter USC citation (e.g., 35 USC 103)"
                          className="w-full"
                        />
                        <Button className="w-full" variant="default">
                          Load USC Section
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Application Selector Modal */}
      <Dialog open={showApplicationSelector} onOpenChange={setShowApplicationSelector}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Select Application</DialogTitle>
            <DialogDescription className="text-gray-600">
              Choose an application to examine
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by application number, title, patent number, or publication number..."
                value={applicationSearchTerm}
                onChange={(e) => setApplicationSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredApplications.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                {applicationSearchTerm ? 'No applications match your search.' : 'No applications available.'}
              </div>
            ) : (
              filteredApplications.map((app: any, index: number) => {
                const appData = app.applicationMetaData || app.application?.applicationMetaData;
                const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
                const hasPatent = appData?.patentNumber;
                const hasPublication = appData?.earliestPublicationNumber;

                return (
                  <button
                    key={appId || index}
                    className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    onClick={() => handleSelectApplication(appId)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">
                          Application: {appId}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {appData?.inventionTitle || 'No title available'}
                        </div>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          {appData?.filingDate && (
                            <span>Filed: {appData.filingDate}</span>
                          )}
                          {appData?.applicationStatusDescriptionText && (
                            <span>Status: {appData.applicationStatusDescriptionText}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 ml-4">
                        {hasPatent && (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            Patent: {appData.patentNumber}
                          </Badge>
                        )}
                        {hasPublication && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                            Pub: {appData.earliestPublicationNumber}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {renderAddNewApplication()}
        </DialogContent>
      </Dialog>

      {/* Viewing Arrangement Modal */}
      <Dialog open={showArrangementModal} onOpenChange={setShowArrangementModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Layout Arrangement</DialogTitle>
            <DialogDescription>
              Choose how to arrange your {numDocsShown} documents
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Show which documents are currently selected */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-900">Active Documents</h4>
              <div className="flex items-center gap-2 flex-wrap">
                {showOfficeAction && (
                  <Badge variant="default" className="text-xs">
                    Office Action
                  </Badge>
                )}
                {showProsDoc && (
                  <Badge variant="default" className="text-xs">
                    Prosecution Doc
                  </Badge>
                )}
                {showReference && (
                  <Badge variant="default" className="text-xs">
                    Reference
                  </Badge>
                )}
                {showTaskResultViewer && (
                  <Badge variant="default" className="text-xs">
                    Task Result
                  </Badge>
                )}
              </div>
            </div>

            {/* Arrangement options */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Layout Style</h4>
              <div className="grid grid-cols-1 gap-3">
                <Button
                  variant={viewArrangement === 'horizontal' ? 'default' : 'outline'}
                  className="h-auto py-4 justify-start"
                  onClick={() => {
                    setViewArrangement('horizontal');
                    setShowArrangementModal(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                      <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                      {numDocsShown >= 3 && (
                        <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                      )}
                      {numDocsShown >= 4 && (
                        <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                      )}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Side-by-Side</div>
                      <div className="text-xs text-muted-foreground">Documents arranged horizontally</div>
                    </div>
                  </div>
                </Button>

                <Button
                  variant={viewArrangement === 'vertical' ? 'default' : 'outline'}
                  className="h-auto py-4 justify-start"
                  onClick={() => {
                    setViewArrangement('vertical');
                    setShowArrangementModal(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <div className="w-12 h-3 bg-primary/20 border border-primary/40 rounded"></div>
                      <div className="w-12 h-3 bg-primary/20 border border-primary/40 rounded"></div>
                      {numDocsShown >= 3 && (
                        <div className="w-12 h-3 bg-primary/20 border border-primary/40 rounded"></div>
                      )}
                      {numDocsShown >= 4 && (
                        <div className="w-12 h-3 bg-primary/20 border border-primary/40 rounded"></div>
                      )}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Stacked</div>
                      <div className="text-xs text-muted-foreground">Documents arranged vertically</div>
                    </div>
                  </div>
                </Button>

                {(numDocsShown === 3 || numDocsShown === 4) && (
                  <Button
                    variant={viewArrangement === 'grid' ? 'default' : 'outline'}
                    className="h-auto py-4 justify-start"
                    onClick={() => {
                      setViewArrangement('grid');
                      setShowArrangementModal(false);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="grid grid-cols-2 gap-1">
                        <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                        <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                        <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                        {numDocsShown === 4 && (
                          <div className="w-6 h-6 bg-primary/20 border border-primary/40 rounded"></div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="font-semibold">Grid</div>
                        <div className="text-xs text-muted-foreground">Documents in a 2x2 grid</div>
                      </div>
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
